import React, { useState, useEffect, useRef } from 'react';
import { storage, awardPoints, subscribeLeaderboard, recordSapLinksCompletion, registerGuestName, updateGuestName, deregisterGuestName } from './storage';
import { db } from './firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import { useTenant } from './TenantContext';
import CouplesTrivia from './CouplesTrivia';

// ─── Utility helpers (unchanged logic) ───────────────────────────────────────
const DAYS_OF_WEEK = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
const MEAL_TYPES = [
  { key: 'breakfast', label: 'Breakfast', icon: '🌅', defaultOpen: '07:00', defaultClose: '10:30' },
  { key: 'lunch',     label: 'Lunch',     icon: '☀️',  defaultOpen: '12:00', defaultClose: '15:00' },
  { key: 'dinner',    label: 'Dinner',    icon: '🌙', defaultOpen: '18:00', defaultClose: '22:00' },
];
const CATEGORY_OPTIONS = [
  { value: 'dining',  label: '🍽️ Dining'  },
  { value: 'pool',    label: '🏊 Pool'    },
  { value: 'spa',     label: '💆 Spa'     },
  { value: 'gym',     label: '🏋️ Gym'     },
  { value: 'amenity', label: '✨ Amenity' },
];

function getCategoryIcon(category) {
  switch (category) {
    case 'dining':  return '🍽️';
    case 'pool':    return '🏊';
    case 'spa':     return '💆';
    case 'gym':     return '🏋️';
    default:        return '✨';
  }
}
function isDiningCategory(c) { return c === 'dining'; }
function isAmenityType(c) { return c === 'pool' || c === 'spa' || c === 'gym' || c === 'amenity'; }
function parseTime(t) { const [h,m] = t.split(':').map(Number); return h*60+m; }
function formatTime12h(t) {
  const [h,m] = t.split(':').map(Number);
  const ap = h >= 12 ? 'PM' : 'AM';
  return `${h%12||12}:${m.toString().padStart(2,'0')} ${ap}`;
}

function getDiningStatus(venue, currentMinutes) {
  const meals = venue.meals || {};
  const enabled = MEAL_TYPES.filter(mt => meals[mt.key]?.enabled);
  if (!enabled.length) return { status:'closed', label:'No meals scheduled', color:'gray' };
  for (const mt of enabled) {
    const meal = meals[mt.key];
    const o = parseTime(meal.open), c = parseTime(meal.close);
    if (currentMinutes >= o && currentMinutes < c) {
      const left = c - currentMinutes;
      return left <= 30
        ? { status:'closing_soon', label:`${mt.label} ends in ${left}m`, color:'yellow' }
        : { status:'open', label:`${mt.label} Now`, color:'green' };
    }
  }
  let nextOpen = Infinity, nextMeal = null;
  for (const mt of enabled) {
    const o = parseTime(meals[mt.key].open);
    if (o > currentMinutes && o < nextOpen) { nextOpen = o; nextMeal = { ...mt, meal: meals[mt.key] }; }
  }
  if (nextMeal) {
    const min = nextOpen - currentMinutes;
    return min <= 120
      ? { status:'opens_soon', label:`${nextMeal.label} at ${formatTime12h(nextMeal.meal.open)}`, color:'blue' }
      : { status:'closed',     label:`${nextMeal.label} at ${formatTime12h(nextMeal.meal.open)}`, color:'gray' };
  }
  const first = enabled.reduce((a,b) => parseTime(meals[a.key].open) < parseTime(meals[b.key].open) ? a : b);
  return { status:'closed', label:`${first.label} tomorrow ${formatTime12h(meals[first.key].open)}`, color:'gray' };
}

function getAmenityStatus(venue, now, currentMinutes) {
  if (venue.hours?.open24) return { status:'open', label:'Open 24 Hours', color:'green' };
  const day = DAYS_OF_WEEK[now.getDay()];
  const hours = venue.hours?.[day] || venue.hours?.default;
  if (!hours || hours.closed) {
    for (let i=1; i<=7; i++) {
      const nd = DAYS_OF_WEEK[(now.getDay()+i)%7];
      const nh = venue.hours?.[nd] || venue.hours?.default;
      if (nh && !nh.closed) return { status:'closed', label: i===1 ? `Opens tomorrow ${formatTime12h(nh.open)}` : `Opens ${nd}`, color:'gray' };
    }
    return { status:'closed', label:'Closed', color:'gray' };
  }
  const o = parseTime(hours.open), c = parseTime(hours.close);
  if (currentMinutes >= o && currentMinutes < c) {
    const left = c - currentMinutes;
    return left <= 30
      ? { status:'closing_soon', label:`Closing in ${left}m`, color:'yellow' }
      : { status:'open', label:'Open Now', color:'green' };
  }
  if (currentMinutes < o) {
    const min = o - currentMinutes;
    return min <= 120
      ? { status:'opens_soon', label:`Opens at ${formatTime12h(hours.open)}`, color:'blue' }
      : { status:'closed',     label:`Opens at ${formatTime12h(hours.open)}`, color:'gray' };
  }
  for (let i=1; i<=7; i++) {
    const nd = DAYS_OF_WEEK[(now.getDay()+i)%7];
    const nh = venue.hours?.[nd] || venue.hours?.default;
    if (nh && !nh.closed) return { status:'closed', label: i===1 ? `Opens tomorrow ${formatTime12h(nh.open)}` : `Opens ${nd}`, color:'gray' };
  }
  return { status:'closed', label:'Closed', color:'gray' };
}

function getVenueStatus(venue) {
  const now = new Date();
  const mins = now.getHours()*60 + now.getMinutes();
  return isDiningCategory(venue.category) ? getDiningStatus(venue, mins) : getAmenityStatus(venue, now, mins);
}

function getStatusSortPriority(s) {
  if (s.status==='open') return 0;
  if (s.status==='closing_soon') return 1;
  if (s.status==='opens_soon') return 2;
  return 3;
}

function isContextuallyRelevant(venue) {
  const h = new Date().getHours(), cat = venue.category;
  if (h>=6&&h<11)  return (cat==='dining'&&venue.meals?.breakfast?.enabled) || cat==='gym';
  if (h>=12&&h<17) return (cat==='dining'&&venue.meals?.lunch?.enabled) || cat==='pool' || cat==='spa';
  if (h>=17&&h<23) return cat==='dining'&&venue.meals?.dinner?.enabled;
  return false;
}

function sortVenues(venues) {
  return [...venues].sort((a,b) => {
    const pa = getStatusSortPriority(getVenueStatus(a));
    const pb = getStatusSortPriority(getVenueStatus(b));
    if (pa !== pb) return pa - pb;
    return (isContextuallyRelevant(a)?0:1) - (isContextuallyRelevant(b)?0:1);
  });
}

function buildGoogleMapsUrl(input) {
  if (!input) return '#';
  const t = input.trim();
  if (t.startsWith('http://') || t.startsWith('https://')) return t;
  const m = t.match(/^(-?\d+\.?\d*)\s*,\s*(-?\d+\.?\d*)$/);
  if (m) return `https://www.google.com/maps?q=${m[1]},${m[2]}`;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(t)}`;
}

// Tenant-driven rank titles — falls back gracefully if config is missing
function getRankTitle(rank, rankTitles) {
  if (!rankTitles || !rankTitles.length) return '';
  for (const rt of rankTitles) {
    if (rt.maxRank === null || rank <= rt.maxRank) return rt.title;
  }
  return rankTitles[rankTitles.length - 1].title;
}

// ─── Name helpers ─────────────────────────────────────────────────────────────
function toTitleCase(str) {
  return str.trim().replace(/\w\S*/g, w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
}
function getInitials(name) {
  return name.split(' ').filter(Boolean).map(n => n[0]).join('').slice(0, 2).toUpperCase();
}

// ─── Main App ────────────────────────────────────────────────────────────────
export default function WeddingApp() {
  const tenant = useTenant();
  const [currentView, setCurrentView] = useState('home');
  const [guestName, setGuestName] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [venues, setVenues] = useState([]);
  const [darkMode, setDarkMode] = useState(() => {
    // If the tenant owns colorMode, initialise from that — don't read localStorage.
    if (tenant.theme.colorMode) return tenant.theme.colorMode === 'dark';
    const saved = localStorage.getItem('theme-mode');
    return saved ? saved === 'dark' : true;
  });

  useEffect(() => {
    // If the tenant has locked colorMode, applyTheme() in TenantProvider already
    // set the correct class — don't touch it here.
    if (tenant.theme.colorMode) return;
    document.documentElement.classList.toggle('dark', darkMode);
    localStorage.setItem('theme-mode', darkMode ? 'dark' : 'light');
  }, [darkMode, tenant.theme.colorMode]);

  // Fire both fetches in parallel on mount so views receive data immediately
  useEffect(() => { loadGuestProfile(); loadSharedData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadGuestProfile = async () => {
    try {
      const profile = await storage.get('guest-profile');
      if (profile?.value) {
        const data = JSON.parse(profile.value);
        setGuestName(data.name);
        setIsAdmin(data.isAdmin || false);
      }
    } catch (e) { console.log('No profile found:', e); }
    setIsLoading(false);
  };

  const loadSharedData = async () => {
    try {
      const [eventsData, venuesData] = await Promise.all([
        storage.get('wedding-events', true),
        storage.get('resort-venues', true),
      ]);
      if (eventsData) setEvents(JSON.parse(eventsData.value));
      if (venuesData) setVenues(JSON.parse(venuesData.value));
    } catch (e) { console.log('Could not preload shared data:', e); }
  };

  const saveGuestProfile = async (name, admin = false) => {
    try {
      const result = await storage.set('guest-profile', JSON.stringify({ name, isAdmin: admin }));
      if (result) { setGuestName(name); setIsAdmin(admin); }
      else alert('Error saving profile. Please try again.');
    } catch (e) { console.error('Error saving profile:', e); alert('Error saving profile: ' + e.message); }
  };

  if (isLoading) return (
    <div className="min-h-screen bg-background-dark flex items-center justify-center">
      <div className="flex flex-col items-center gap-3">
        <span className="material-symbols-outlined text-primary" style={{fontSize:'48px'}}>{tenant.theme.icon}</span>
        <p className="text-primary/70 text-sm font-semibold tracking-widest uppercase">Loading...</p>
      </div>
    </div>
  );

  if (!guestName) return <GuestSetup onSave={saveGuestProfile} />;

  return (
    <div className="min-h-screen bg-background-dark">
      <div className="max-w-md mx-auto min-h-screen flex flex-col relative">
        {/* Sticky Header */}
        <header className="flex items-center bg-background-dark/80 backdrop-blur-md sticky top-0 z-50 px-4 py-3 justify-between border-b border-primary/10">
          <button
          onClick={() => setCurrentView('home')}
          className="flex size-10 shrink-0 items-center justify-center bg-primary/20 rounded-full">
            <span className="material-symbols-outlined text-primary">{tenant.theme.icon}</span>
          </button>
          <h2 className="text-slate-100 text-lg font-bold leading-tight tracking-tight flex-1 ml-3">{tenant.appName}</h2>
          <div className="flex items-center gap-2">
<div className="flex size-10 items-center justify-center rounded-full bg-primary/20 border border-primary/30">
              <span className="text-primary font-bold text-sm">{getInitials(guestName)}</span>
            </div>
          </div>
        </header>

        {/* View Content */}
        <div className="flex-1 overflow-y-auto pb-20">
          {currentView === 'home'        && <HomeView guestName={guestName} isAdmin={isAdmin} setCurrentView={setCurrentView} events={events} venues={venues} onVenuesChange={setVenues} />}
          {currentView === 'events'      && <EventsView isAdmin={isAdmin} events={events} onEventsChange={setEvents} />}
          {currentView === 'games'       && <GamesView guestName={guestName} isAdmin={isAdmin} />}
          {currentView === 'leaderboard' && <LeaderboardView guestName={guestName} />}
          {currentView === 'settings'    && <SettingsView guestName={guestName} isAdmin={isAdmin} onNameChange={saveGuestProfile} darkMode={darkMode} onThemeChange={()=>setDarkMode(m=>!m)} />}
        </div>

        <BottomNav currentView={currentView} setCurrentView={setCurrentView} />
      </div>
    </div>
  );
}

// ─── Guest Setup ─────────────────────────────────────────────────────────────
function GuestSetup({ onSave }) {
  const tenant = useTenant();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName]   = useState('');
  const [adminCode, setAdminCode] = useState('');
  const [error, setError]         = useState('');
  const [checking, setChecking]   = useState(false);
  const [nameTaken, setNameTaken] = useState(false);

  const handleSubmit = async () => {
    const fn = toTitleCase(firstName);
    const ln = toTitleCase(lastName);
    if (!fn || !ln) { setError('Please enter both your first and last name.'); return; }
    const fullName = `${fn} ${ln}`;
    setError('');
    setNameTaken(false);
    setChecking(true);
    try {
      const result = await registerGuestName(fullName);
      if (!result.success) {
        setError("This name is already registered.");
        setNameTaken(true);
        setChecking(false);
        return;
      }
    } catch (e) {
      // Allow through on Firestore outage — uniqueness check is a soft guard
      console.warn('Could not check name registry, proceeding anyway:', e);
    }
    setChecking(false);
    onSave(fullName, adminCode === tenant.adminCode);
  };

  const handleClaimAsMe = () => {
    const fn = toTitleCase(firstName);
    const ln = toTitleCase(lastName);
    const fullName = `${fn} ${ln}`;
    onSave(fullName, adminCode === tenant.adminCode);
  };

  return (
    <div className="min-h-screen bg-mesh flex items-center justify-center p-4">
      <div className="bg-primary/5 border border-primary/20 rounded-2xl p-8 max-w-md w-full shadow-2xl shadow-primary/10">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="bg-primary/20 p-4 rounded-full">
              <span className="material-symbols-outlined text-primary" style={{fontSize:'40px'}}>{tenant.theme.icon}</span>
            </div>
          </div>
          <h1 className="text-3xl font-bold text-primary mb-2">{tenant.appName}</h1>
          <p className="text-primary/60 text-sm">{tenant.theme.motifText}</p>
        </div>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-primary/50 mb-2">First Name</label>
              <input
                type="text" value={firstName}
                onChange={e => setFirstName(e.target.value)}
                onKeyPress={e => e.key==='Enter' && handleSubmit()}
                className="w-full px-4 py-3 bg-white/5 border border-primary/20 rounded-xl text-primary placeholder-slate-600 focus:outline-none focus:border-primary"
                placeholder="First"
              />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-primary/50 mb-2">Last Name</label>
              <input
                type="text" value={lastName}
                onChange={e => setLastName(e.target.value)}
                onKeyPress={e => e.key==='Enter' && handleSubmit()}
                className="w-full px-4 py-3 bg-white/5 border border-primary/20 rounded-xl text-primary placeholder-slate-600 focus:outline-none focus:border-primary"
                placeholder="Last"
              />
            </div>
          </div>
          {error && (
            <div className="space-y-2">
              <p className="text-red-400 text-sm">{error}</p>
              {nameTaken && (
                <button
                  type="button"
                  onClick={handleClaimAsMe}
                  className="w-full bg-white/5 border border-primary/20 text-primary py-2 rounded-xl text-sm font-semibold hover:bg-primary/10 transition"
                >
                  That's me on a new device — sign me in
                </button>
              )}
            </div>
          )}
          <div>
            <label className="block text-xs font-bold uppercase tracking-widest text-primary/50 mb-2">
              Admin Code <span className="text-primary/30 font-normal normal-case">(optional)</span>
            </label>
            <input
              type="password" value={adminCode}
              onChange={e => setAdminCode(e.target.value)}
              onKeyPress={e => e.key==='Enter' && handleSubmit()}
              className="w-full px-4 py-3 bg-white/5 border border-primary/20 rounded-xl text-primary placeholder-slate-600 focus:outline-none focus:border-primary"
              placeholder="Leave blank if guest"
            />
          </div>
          <button
            onClick={handleSubmit}
            disabled={checking}
            className="w-full bg-primary text-white py-3 rounded-xl font-bold hover:opacity-90 transition shadow-lg shadow-primary/20 disabled:opacity-60"
          >
            {checking ? 'Checking…' : 'Join the Celebration'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Home View ────────────────────────────────────────────────────────────────
function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

function HomeView({ guestName, isAdmin, setCurrentView, events, venues, onVenuesChange }) {
  const tenant = useTenant();
  const isLight = tenant.theme.colorMode === 'light';
  const heroNameClass = tenant.theme.cursiveHeroName
    ? "font-['Allison',cursive] text-4xl leading-none"
    : "font-display text-2xl font-bold leading-tight";
  const upcomingEvent = [...events]
    .filter(e => {
      const endDt = new Date(e.date + 'T' + (e.endTime || '23:59'));
      return endDt >= new Date();
    })
    .sort((a, b) => new Date(a.date + 'T' + (a.time || '00:00')) - new Date(b.date + 'T' + (b.time || '00:00')))[0] || null;

  return (
    <div className="bg-mesh min-h-full">
      {/* Hero — photo mode or icon mode */}
      <div className="px-4 pt-6 pb-2">
        {tenant.theme.heroImageUrl ? (
          // Photo hero — image banner, greeting text flows below
          <div>
            <div className="overflow-hidden rounded-xl h-44 mb-4">
              <img src={tenant.theme.heroImageUrl} alt="couple" className="w-full h-full object-cover" />
            </div>
            <div className="px-1">
              <p className={`text-sm font-semibold mb-0.5 ${isLight ? 'text-slate-600' : 'text-white/70'}`}>{getGreeting()},</p>
              <h1 className={`${heroNameClass} ${isLight ? 'text-slate-800' : 'text-white'}`}>{guestName}</h1>
              <p className="text-primary/70 text-sm mt-2">Time to Celebrate!</p>
            </div>
          </div>
        ) : (
          // Icon hero — icon sits inline to the right of the greeting text
          <div className="rounded-xl bg-primary/10 p-6 flex items-start justify-between gap-4">
            <div>
              <p className={`text-sm font-semibold mb-0.5 ${isLight ? 'text-slate-600' : 'text-white/70'}`}>{getGreeting()},</p>
              <h1 className={`${heroNameClass} ${isLight ? 'text-slate-800' : 'text-white'}`}>{guestName}</h1>
              <p className="text-primary/70 text-sm mt-2">Time to Celebrate!</p>
            </div>
            <span className="material-symbols-outlined text-primary opacity-70 flex-shrink-0" style={{fontSize:'52px'}}>{tenant.theme.heroIcon || tenant.theme.icon || 'celebration'}</span>
          </div>
        )}
      </div>

      {/* Next Event */}
      <div className="px-4 py-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-white text-xl font-bold">Next Event</h2>
          <span className="text-xs font-bold px-2 py-1 bg-primary text-white rounded-full">UPCOMING</span>
        </div>
        {upcomingEvent ? (
          <div className="rounded-xl overflow-hidden bg-white/5 border border-primary/10 shadow-lg">
            <div className="h-36 relative flex items-end p-4">
              {upcomingEvent.imageUrl ? (
                <img src={upcomingEvent.imageUrl} alt={upcomingEvent.name} className="absolute inset-0 w-full h-full object-cover" />
              ) : (
                <div className="absolute inset-0" style={upcomingEvent.color1
                  ? { background: `linear-gradient(135deg, ${upcomingEvent.color1}99 0%, ${(upcomingEvent.color2 || upcomingEvent.color1)}55 55%, #1b1022 100%)` }
                  : { background: 'linear-gradient(135deg, rgba(147,13,242,0.4) 0%, rgba(88,28,135,0.4) 50%, #1b1022 100%)' }} />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
              <span className="relative z-10 text-[10px] font-bold text-white on-dark-bg uppercase tracking-widest bg-black/50 px-2 py-1 rounded-full">
                {new Date(upcomingEvent.date + 'T00:00:00').toLocaleDateString('en-US', { weekday:'short', month:'short', day:'numeric' })}
              </span>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <p className="text-primary text-xs font-bold uppercase tracking-wider">{upcomingEvent.time}{upcomingEvent.endTime ? ` – ${upcomingEvent.endTime}` : ''}</p>
                <h3 className="text-white text-2xl font-bold leading-tight">{upcomingEvent.name}</h3>
                {upcomingEvent.eventType && <p className="text-slate-400 text-sm font-medium mt-0.5">{upcomingEvent.eventType}</p>}
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center gap-2 text-slate-400">
                  <span className="material-symbols-outlined text-primary/70" style={{fontSize:'16px'}}>location_on</span>
                  {upcomingEvent.venueCoords ? (
                    <a href={buildGoogleMapsUrl(upcomingEvent.venueCoords)} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline">
                      {upcomingEvent.venue} ↗
                    </a>
                  ) : (
                    <span className="text-sm">{upcomingEvent.venue}</span>
                  )}
                </div>
                {upcomingEvent.dressCode && (
                  <div className="flex items-center gap-2 text-slate-400">
                    <span className="material-symbols-outlined text-primary/70" style={{fontSize:'16px'}}>checkroom</span>
                    <span className="text-sm">Dress Code: <span className="text-primary font-medium">{upcomingEvent.dressCode}</span></span>
                  </div>
                )}
                {upcomingEvent.vibes && (
                  <div className="flex items-center gap-2 text-slate-400">
                    <span className="material-symbols-outlined text-primary/70" style={{fontSize:'16px'}}>auto_awesome</span>
                    <span className="text-sm">Vibes: <span className="text-primary font-medium">{upcomingEvent.vibes}</span></span>
                  </div>
                )}
              </div>
              <button
                onClick={() => setCurrentView('events')}
                className="w-full flex items-center justify-center rounded-xl h-10 bg-primary text-white text-sm font-bold shadow-md shadow-primary/20"
              >
                View Schedule
              </button>
            </div>
          </div>
        ) : (
          <div className="rounded-xl bg-white/5 border border-primary/10 p-8 text-center">
            <span className="material-symbols-outlined text-primary/30" style={{fontSize:'40px'}}>event</span>
            <p className="text-slate-400 mt-2 text-sm">No upcoming events</p>
            {isAdmin && <p className="text-primary/50 text-xs mt-1">Add events in the Events tab</p>}
          </div>
        )}
      </div>

      {/* Resort Amenities */}
      <div className="px-4 pb-2">
        <AroundTheResort isAdmin={isAdmin} venues={venues} onVenuesChange={onVenuesChange} label={tenant.theme.amenitiesLabel} />
      </div>

      {/* Admin Notice */}
      {isAdmin && (
        <div className="px-4 py-2">
          <div className="bg-primary/10 border border-primary/20 rounded-xl p-4 flex items-start gap-3">
            <span className="material-symbols-outlined text-primary mt-0.5" style={{fontSize:'18px'}}>admin_panel_settings</span>
            <div>
              <p className="text-primary text-sm font-bold">Admin Access Enabled</p>
              <p className="text-primary/60 text-xs mt-0.5">Manage events, games, and venues from their respective tabs.</p>
            </div>
          </div>
        </div>
      )}

      {/* Motif */}
      <div className="flex justify-center py-10 opacity-30">
        <div className="flex flex-col items-center gap-2">
          <span className="material-symbols-outlined text-primary" style={{fontSize:'48px'}}>{tenant.theme.motifIcon}</span>
          <p className="text-xs font-bold text-primary uppercase tracking-[0.2em]">{tenant.theme.motifText}</p>
        </div>
      </div>
    </div>
  );
}

// ─── Around the Resort ────────────────────────────────────────────────────────
function AroundTheResort({ isAdmin, venues, onVenuesChange, label = 'Around the Resort' }) {
  const [expanded, setExpanded] = useState(false);
  const [filter, setFilter] = useState('open');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingVenue, setEditingVenue] = useState(null);
  const [, setTick] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setTick(t => t+1), 60000);
    return () => clearInterval(id);
  }, []);

  const saveVenues = async (v) => {
    try { await storage.set('resort-venues', JSON.stringify(v), true); onVenuesChange(v); }
    catch (e) { console.error(e); }
  };

  const addVenue    = d => saveVenues([...venues, { id:Date.now().toString(), ...d, createdAt:new Date().toISOString() }]).then(() => setShowAddForm(false));
  const updateVenue = d => saveVenues(venues.map(v => v.id===editingVenue.id ? {...v,...d} : v)).then(() => setEditingVenue(null));
  const deleteVenue = id => { if (window.confirm('Delete this venue?')) saveVenues(venues.filter(v => v.id!==id)); };

  const dining   = venues.filter(v => isDiningCategory(v.category));
  const amenity  = venues.filter(v => isAmenityType(v.category));
  const openD    = dining.filter(v => { const s=getVenueStatus(v).status; return s==='open'||s==='closing_soon'; }).length;
  const openA    = amenity.filter(v => { const s=getVenueStatus(v).status; return s==='open'||s==='closing_soon'; }).length;

  const filtered = filter==='all'
    ? sortVenues(venues)
    : filter==='open'
    ? sortVenues(venues.filter(v => { const s=getVenueStatus(v).status; return s==='open'||s==='closing_soon'; }))
    : sortVenues(venues.filter(v => { const s=getVenueStatus(v).status; return s!=='open'&&s!=='closing_soon'; }));

  return (
    <div className="rounded-xl border border-primary/10 overflow-hidden bg-white/5">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-primary/5 transition"
      >
        <div className="flex items-center gap-3">
          <div className="bg-primary/20 rounded-full p-2">
            <span className="material-symbols-outlined text-primary" style={{fontSize:'20px'}}>hotel</span>
          </div>
          <div className="text-left">
            <h3 className="font-bold text-slate-100 text-sm">{label}</h3>
            <p className="text-xs text-slate-400 mt-0.5">{openD} dining · {openA} amenities open now</p>
          </div>
        </div>
        <span className="material-symbols-outlined text-slate-400" style={{fontSize:'20px'}}>
          {expanded ? 'expand_less' : 'expand_more'}
        </span>
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-3">
          <div className="flex gap-2 flex-wrap">
            {['open','all','closed'].map(f => (
              <button key={f} onClick={() => setFilter(f)}
                className={`px-3 py-1 rounded-full text-xs font-bold transition ${
                  filter===f ? 'bg-primary text-white' : 'bg-primary/10 text-primary/70 hover:bg-primary/20'
                }`}
              >
                {f==='open'?'Open Now':f==='all'?'All':'Closed'}
              </button>
            ))}
            {isAdmin && (
              <button
                onClick={() => { setShowAddForm(!showAddForm); setEditingVenue(null); }}
                className="ml-auto bg-primary/20 text-primary p-1 rounded-lg hover:bg-primary/30 transition"
              >
                <span className="material-symbols-outlined" style={{fontSize:'18px'}}>add</span>
              </button>
            )}
          </div>

          {(showAddForm || editingVenue) && isAdmin && (
            <VenueForm
              venue={editingVenue}
              onSave={editingVenue ? updateVenue : addVenue}
              onCancel={() => { setShowAddForm(false); setEditingVenue(null); }}
            />
          )}

          {venues.length === 0 ? (
            <div className="text-center py-8">
              <span className="material-symbols-outlined text-primary/30" style={{fontSize:'32px'}}>location_on</span>
              <p className="text-sm text-slate-400 mt-2">No venues added yet</p>
              {isAdmin && <p className="text-xs text-primary/50 mt-1">Tap + to add resort venues</p>}
            </div>
          ) : filtered.length === 0 ? (
            <p className="text-center text-sm text-slate-400 py-4">No {filter==='open'?'open':'closed'} venues right now</p>
          ) : (
            <div className="space-y-2">
              {filtered.map(v => (
                <VenueCard key={v.id} venue={v} isAdmin={isAdmin}
                  onEdit={() => { setEditingVenue(v); setShowAddForm(false); }}
                  onDelete={deleteVenue}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function VenueCard({ venue, isAdmin, onEdit, onDelete }) {
  const s = getVenueStatus(venue);
  const now = new Date();
  const isDining = isDiningCategory(venue.category);
  const todayHours = !isDining ? (venue.hours?.[DAYS_OF_WEEK[now.getDay()]] || venue.hours?.default) : null;

  const dotColor = { green:'bg-green-400', yellow:'bg-yellow-400', blue:'bg-blue-400', gray:'bg-slate-600' };
  const txtColor = { green:'text-green-400', yellow:'text-yellow-400', blue:'text-blue-400', gray:'text-slate-500' };
  const isClosed = s.status==='closed';

  const meals = isDining ? MEAL_TYPES.filter(mt => venue.meals?.[mt.key]?.enabled).map(mt => {
    const m = venue.meals[mt.key];
    const mins = now.getHours()*60+now.getMinutes();
    return { ...mt, meal:m, isActive: mins>=parseTime(m.open)&&mins<parseTime(m.close) };
  }) : [];

  const costBadge = venue.costType==='included'
    ? { label:'Included With Stay', cls:'bg-green-900/40 text-green-400' }
    : venue.costType==='reservation'
    ? { label:'Reservation Required', cls:'bg-primary/20 text-primary' }
    : venue.costType==='additional'
    ? { label:'Additional Cost', cls:'bg-primary/20 text-primary' }
    : null;

  return (
    <div className={`border rounded-xl p-3 transition ${
      !isClosed ? 'border-primary/20 bg-primary/5' : 'border-white/5 bg-white/3'
    }`}>
      <div className="flex justify-between items-start">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`font-semibold text-sm ${isClosed ? 'text-slate-500' : 'text-slate-100'}`}>
              {getCategoryIcon(venue.category)} {venue.name}
            </span>
            <span className={`flex items-center gap-1 text-xs font-medium ${txtColor[s.color]}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${dotColor[s.color]}`} />
              {s.label}
            </span>
          </div>

          {!isDining && venue.hours?.open24 && (
            <p className="text-xs text-green-400 mt-1 flex items-center gap-1">
              <span className="material-symbols-outlined" style={{fontSize:'12px'}}>schedule</span> Open 24 Hours
            </p>
          )}
          {!isDining && !venue.hours?.open24 && todayHours && !todayHours.closed && (
            <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
              <span className="material-symbols-outlined" style={{fontSize:'12px'}}>schedule</span>
              {formatTime12h(todayHours.open)} – {formatTime12h(todayHours.close)}
            </p>
          )}

          {isDining && meals.length > 0 && (
            <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1">
              {meals.map(item => (
                <span key={item.key} className={`text-xs flex items-center gap-1 ${item.isActive?'text-green-400 font-medium':isClosed?'text-slate-600':'text-slate-500'}`}>
                  {item.icon} {formatTime12h(item.meal.open)}–{formatTime12h(item.meal.close)}
                </span>
              ))}
            </div>
          )}

          {venue.description && <p className={`text-xs mt-1 ${isClosed?'text-slate-600':'text-slate-400'}`}>{venue.description}</p>}
          {costBadge && <span className={`inline-block text-xs px-2 py-0.5 rounded-full font-medium mt-1.5 ${costBadge.cls}`}>{costBadge.label}</span>}
        </div>

        {isAdmin && (
          <div className="flex gap-1 ml-2 flex-shrink-0">
            <button onClick={onEdit} className="text-slate-500 hover:text-primary p-1 rounded transition">
              <span className="material-symbols-outlined" style={{fontSize:'16px'}}>edit</span>
            </button>
            <button onClick={() => onDelete(venue.id)} className="text-slate-500 hover:text-red-400 p-1 rounded transition">
              <span className="material-symbols-outlined" style={{fontSize:'16px'}}>close</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function VenueForm({ venue, onSave, onCancel }) {
  const defaultMeals = {
    breakfast: { enabled:true, open:'07:00', close:'10:30' },
    lunch:     { enabled:true, open:'12:00', close:'15:00' },
    dinner:    { enabled:true, open:'18:00', close:'22:00' },
  };
  const [form, setForm] = useState({
    name:       venue?.name || '',
    category:   venue?.category || 'dining',
    description:venue?.description || '',
    costType:   venue?.costType || 'included',
    hoursMode:  venue?.hours?.open24 ? '24h' : venue?.hours?.default ? 'same' : 'different',
    hours:      venue?.hours || { default: { open:'08:00', close:'22:00' } },
    meals:      venue?.meals || defaultMeals,
  });
  const isDining = isDiningCategory(form.category);

  const handleSubmit = e => {
    e.preventDefault();
    if (!form.name.trim()) return;
    const save = { name:form.name.trim(), category:form.category, description:form.description.trim(), costType:form.costType };
    if (isDining) {
      save.meals = form.meals; save.hours = null;
    } else {
      const h = {};
      if (form.hoursMode==='24h') { h.open24=true; }
      else if (form.hoursMode==='same') { h.default = form.hours.default || { open:'08:00', close:'22:00' }; }
      else { DAYS_OF_WEEK.forEach(d => { if (form.hours[d]) h[d]=form.hours[d]; }); if (!Object.keys(h).length) h.default={open:'08:00',close:'22:00'}; }
      save.hours = h; save.meals = null;
    }
    onSave(save);
  };

  const updateDayHours = (day, field, val) =>
    setForm(p => ({ ...p, hours: { ...p.hours, [day]: { ...(p.hours[day]||{open:'08:00',close:'22:00'}), [field]:val } } }));
  const toggleDayClosed = day =>
    setForm(p => { const cur=p.hours[day]; return cur?.closed ? { ...p, hours:{...p.hours,[day]:{open:'08:00',close:'22:00'}} } : { ...p, hours:{...p.hours,[day]:{closed:true}} }; });
  const updateMeal = (k,f,v) =>
    setForm(p => ({ ...p, meals:{ ...p.meals, [k]:{ ...p.meals[k], [f]:v } } }));
  const toggleMeal = k =>
    setForm(p => ({ ...p, meals:{ ...p.meals, [k]:{ ...p.meals[k], enabled:!p.meals[k]?.enabled, open:p.meals[k]?.open||MEAL_TYPES.find(m=>m.key===k).defaultOpen, close:p.meals[k]?.close||MEAL_TYPES.find(m=>m.key===k).defaultClose } } }));

  const inputCls = "w-full px-3 py-2 bg-white/5 border border-primary/20 rounded-lg text-sm text-white placeholder-slate-600 focus:outline-none focus:border-primary";
  const selectCls = "px-3 py-2 bg-white/5 border border-primary/20 rounded-lg text-sm text-white focus:outline-none focus:border-primary";

  return (
    <form onSubmit={handleSubmit} className="bg-primary/5 border border-primary/20 rounded-xl p-4 space-y-3">
      <h3 className="font-bold text-slate-100 text-sm">{venue ? 'Edit Venue' : 'Add Venue'}</h3>
      <input type="text" placeholder="Venue name" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} className={inputCls} required />
      <div className="grid grid-cols-2 gap-2">
        <select value={form.category} onChange={e=>setForm({...form,category:e.target.value})} className={selectCls}>
          {CATEGORY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select value={form.costType} onChange={e=>setForm({...form,costType:e.target.value})} className={selectCls}>
          <option value="included">Included</option>
          <option value="reservation">Reservation Required</option>
          <option value="additional">Additional Cost</option>
        </select>
      </div>
      <textarea placeholder="Description (optional)" value={form.description} onChange={e=>setForm({...form,description:e.target.value})} className={inputCls} rows="2" />

      {isDining && (
        <div className="space-y-2">
          <label className="text-xs font-bold text-primary/60 uppercase tracking-widest">Meal Schedule</label>
          {MEAL_TYPES.map(mt => {
            const m = form.meals[mt.key]||{};
            return (
              <div key={mt.key} className={`rounded-lg p-2.5 ${m.enabled?'bg-primary/10 border border-primary/20':'bg-white/5'}`}>
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={!!m.enabled} onChange={()=>toggleMeal(mt.key)} className="accent-primary" />
                    <span className={`text-xs font-semibold ${m.enabled?'text-slate-100':'text-slate-500'}`}>{mt.icon} {mt.label}</span>
                  </label>
                  {m.enabled && (
                    <div className="flex items-center gap-1">
                      <input type="time" value={m.open||mt.defaultOpen} onChange={e=>updateMeal(mt.key,'open',e.target.value)} className="px-1.5 py-0.5 bg-white/5 border border-primary/20 rounded text-xs text-white w-24" />
                      <span className="text-slate-500 text-xs">–</span>
                      <input type="time" value={m.close||mt.defaultClose} onChange={e=>updateMeal(mt.key,'close',e.target.value)} className="px-1.5 py-0.5 bg-white/5 border border-primary/20 rounded text-xs text-white w-24" />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!isDining && (
        <div className="space-y-2">
          <label className="text-xs font-bold text-primary/60 uppercase tracking-widest">Operating Hours</label>
          <div className="flex gap-2 flex-wrap">
            {['same','different','24h'].map(m => (
              <button key={m} type="button" onClick={()=>setForm({...form,hoursMode:m})}
                className={`px-3 py-1 rounded-full text-xs font-bold transition ${form.hoursMode===m?'bg-primary text-white':'bg-primary/10 text-primary/70 hover:bg-primary/20'}`}>
                {m==='same'?'Same Every Day':m==='different'?'By Day':'24 Hours'}
              </button>
            ))}
          </div>
          {form.hoursMode==='24h' && <p className="text-xs text-green-400 bg-green-900/20 rounded-lg px-3 py-2">Open at all times</p>}
          {form.hoursMode==='same' && (
            <div className="flex items-center gap-2">
              <input type="time" value={form.hours.default?.open||'08:00'} onChange={e=>setForm({...form,hours:{...form.hours,default:{...form.hours.default,open:e.target.value}}})} className="px-2 py-1 bg-white/5 border border-primary/20 rounded-lg text-xs text-white flex-1" />
              <span className="text-slate-500 text-xs">to</span>
              <input type="time" value={form.hours.default?.close||'22:00'} onChange={e=>setForm({...form,hours:{...form.hours,default:{...form.hours.default,close:e.target.value}}})} className="px-2 py-1 bg-white/5 border border-primary/20 rounded-lg text-xs text-white flex-1" />
            </div>
          )}
          {form.hoursMode==='different' && (
            <div className="space-y-1.5 max-h-48 overflow-y-auto">
              {DAYS_OF_WEEK.map(day => {
                const dh = form.hours[day];
                return (
                  <div key={day} className="flex items-center gap-2">
                    <span className="text-xs font-medium text-slate-400 w-10">{day.slice(0,3)}</span>
                    {dh?.closed ? <span className="text-xs text-slate-500 flex-1">Closed</span> : (
                      <>
                        <input type="time" value={dh?.open||'08:00'} onChange={e=>updateDayHours(day,'open',e.target.value)} className="px-1 py-0.5 bg-white/5 border border-primary/20 rounded text-xs text-white flex-1" />
                        <span className="text-slate-500 text-xs">–</span>
                        <input type="time" value={dh?.close||'22:00'} onChange={e=>updateDayHours(day,'close',e.target.value)} className="px-1 py-0.5 bg-white/5 border border-primary/20 rounded text-xs text-white flex-1" />
                      </>
                    )}
                    <button type="button" onClick={()=>toggleDayClosed(day)}
                      className={`text-xs px-2 py-0.5 rounded ${dh?.closed?'bg-primary/20 text-primary':'bg-red-900/40 text-red-400'}`}>
                      {dh?.closed?'Open':'Close'}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div className="flex gap-2 pt-1">
        <button type="submit" className="flex-1 bg-primary text-white py-2 rounded-xl font-bold text-sm hover:opacity-90 transition">
          {venue ? 'Save Changes' : 'Add Venue'}
        </button>
        <button type="button" onClick={onCancel} className="px-4 bg-white/5 border border-white/10 text-slate-300 py-2 rounded-xl font-bold text-sm">
          Cancel
        </button>
      </div>
    </form>
  );
}

// ─── Events View ──────────────────────────────────────────────────────────────
function EventsView({ isAdmin, events, onEventsChange }) {
  const tenant = useTenant();
  const multiDay = tenant.events?.multiDay !== false; // default true
  const listView = tenant.events?.listView === true;  // grouped scrollable list when true
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);

  const saveEvents = async (evs) => {
    try { await storage.set('wedding-events', JSON.stringify(evs), true); onEventsChange(evs); }
    catch (e) { console.error(e); }
  };

  const addEvent    = d => saveEvents([...events, { id:Date.now().toString(), ...d, createdAt:new Date().toISOString() }]).then(()=>setShowAddForm(false));
  const updateEvent = d => saveEvents(events.map(e => e.id===editingEvent.id?{...e,...d}:e)).then(()=>setEditingEvent(null));
  const deleteEvent = id => { if(window.confirm('Delete this event?')) saveEvents(events.filter(e=>e.id!==id)); };

  const sorted = [...events].sort((a, b) => {
    const dateComp = new Date(a.date) - new Date(b.date);
    if (dateComp !== 0) return dateComp;
    return (a.time || '').localeCompare(b.time || ''); // HH:MM strings sort correctly lexicographically
  });
  const uniqueDates = [...new Set(sorted.map(e=>e.date))];
  const activeDate = multiDay ? (selectedDate || uniqueDates[0] || null) : null;
  const shown = (multiDay && activeDate) ? sorted.filter(e=>e.date===activeDate) : sorted;

  const formatDate = d => new Date(d+'T00:00:00').toLocaleDateString('en-US', { weekday:'long', month:'long', day:'numeric' });
  const formatShortDate = d => new Date(d+'T00:00:00').toLocaleDateString('en-US', { weekday:'long', month:'short', day:'numeric' }).toUpperCase();
  const dayLabel = (d, i) => `Day ${i+1}`;
  const DAY_WORDS = ['One','Two','Three','Four','Five','Six','Seven','Eight','Nine','Ten'];
  const dayWordLabel = i => `Day ${DAY_WORDS[i] || i+1}`;

  // Grouped scrollable list view (multiDay + listView)
  if (multiDay && listView) {
    return (
      <div>
        {/* Header row */}
        <div className="flex items-center justify-between px-4 pt-5 pb-2">
          <h2 className="text-white text-2xl font-bold">{tenant.events?.screenTitle || 'Events'}</h2>
          {isAdmin && (
            <button onClick={() => { setShowAddForm(!showAddForm); setEditingEvent(null); }}
              className="bg-primary/20 text-primary p-2 rounded-xl hover:bg-primary/30 transition border border-primary/20">
              <span className="material-symbols-outlined" style={{fontSize:'20px'}}>add</span>
            </button>
          )}
        </div>

        <div className="px-4 pt-2 space-y-8">
          {showAddForm && !editingEvent && <EventForm onSave={addEvent} onCancel={()=>setShowAddForm(false)} />}
          {editingEvent && <EventForm event={editingEvent} onSave={updateEvent} onCancel={()=>setEditingEvent(null)} />}

          {events.length === 0 ? (
            <div className="text-center py-16">
              <span className="material-symbols-outlined text-primary/30" style={{fontSize:'48px'}}>calendar_month</span>
              <p className="text-slate-400 mt-3">No events yet</p>
              {isAdmin && <p className="text-primary/50 text-sm mt-1">Tap + to add your first event</p>}
            </div>
          ) : (
            uniqueDates.map((date, i) => {
              const dayEvents = sorted.filter(e => e.date === date);
              return (
                <div key={date}>
                  {/* Day group header: "Day One" cursive left, "FRIDAY, OCT 18" right */}
                  <div className="flex items-baseline justify-between mb-4">
                    <span className="font-['Allison',cursive] text-primary" style={{fontSize:'40px', lineHeight:1.1}}>
                      {dayWordLabel(i)}
                    </span>
                    <span className="text-xs font-bold tracking-widest text-slate-500 uppercase">
                      {formatShortDate(date)}
                    </span>
                  </div>
                  <div className="space-y-4">
                    {dayEvents.map(event => (
                      <EventCard key={event.id} event={event} isAdmin={isAdmin}
                        onDelete={deleteEvent} onEdit={() => { setEditingEvent(event); setShowAddForm(false); }}
                      />
                    ))}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header row */}
      <div className="flex items-center justify-between px-4 pt-5 pb-2">
        <h2 className="text-white text-2xl font-bold">{tenant.events?.screenTitle || 'Events'}</h2>
        {isAdmin && (
          <button onClick={() => { setShowAddForm(!showAddForm); setEditingEvent(null); }}
            className="bg-primary/20 text-primary p-2 rounded-xl hover:bg-primary/30 transition border border-primary/20">
            <span className="material-symbols-outlined" style={{fontSize:'20px'}}>add</span>
          </button>
        )}
      </div>

      {/* Day tabs — only shown in multi-day mode with more than one date */}
      {multiDay && uniqueDates.length > 1 && (
        <div className="flex border-b border-primary/10 px-4">
          {uniqueDates.map((d,i) => (
            <button key={d} onClick={() => setSelectedDate(d)}
              className={`flex-1 py-3 text-sm font-bold border-b-2 transition ${
                activeDate===d ? 'border-primary text-primary' : 'border-transparent text-slate-500 hover:text-slate-300'
              }`}
            >
              {dayLabel(d,i)}
            </button>
          ))}
        </div>
      )}

      <div className="px-4 pt-4 space-y-4">
        {/* Date label — only in multi-day mode */}
        {multiDay && activeDate && (
          <div className="flex items-center gap-2 mb-2">
            <span className="material-symbols-outlined text-primary" style={{fontSize:'20px'}}>calendar_today</span>
            <h3 className={`text-white text-lg font-bold ${tenant.events?.cursiveDayHeaders ? "font-['Allison',cursive] text-3xl" : ''}`}>
              {formatDate(activeDate)}
            </h3>
          </div>
        )}

        {showAddForm && !editingEvent && <EventForm onSave={addEvent} onCancel={()=>setShowAddForm(false)} />}
        {editingEvent && <EventForm event={editingEvent} onSave={updateEvent} onCancel={()=>setEditingEvent(null)} />}

        {events.length === 0 ? (
          <div className="text-center py-16">
            <span className="material-symbols-outlined text-primary/30" style={{fontSize:'48px'}}>calendar_month</span>
            <p className="text-slate-400 mt-3">No events yet</p>
            {isAdmin && <p className="text-primary/50 text-sm mt-1">Tap + to add your first event</p>}
          </div>
        ) : shown.length === 0 ? (
          <p className="text-center text-slate-400 py-8 text-sm">No events for this day</p>
        ) : (
          shown.map(event => (
            <EventCard key={event.id} event={event} isAdmin={isAdmin}
              onDelete={deleteEvent} onEdit={() => { setEditingEvent(event); setShowAddForm(false); }}
            />
          ))
        )}
      </div>
    </div>
  );
}

function EventCard({ event, isAdmin, onDelete, onEdit }) {
  const [expanded, setExpanded] = useState(false);

  const gradientStyle = event.color1
    ? { background: `linear-gradient(135deg, ${event.color1}99 0%, ${(event.color2 || event.color1)}55 55%, #1b1022 100%)` }
    : { background: 'linear-gradient(135deg, rgba(147,13,242,0.3) 0%, rgba(88,28,135,0.3) 50%, #1b1022 100%)' };

  return (
    <div className="rounded-xl overflow-hidden bg-white/5 border border-primary/10 shadow-lg">
      {/* Card header — full-bleed photo if imageUrl set, otherwise colour gradient */}
      <div className="h-32 relative">
        {event.imageUrl ? (
          <img src={event.imageUrl} alt={event.name} className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0" style={gradientStyle} />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        {event.vibes && (
          <div className="absolute bottom-3 left-4">
            <span className={`text-[10px] font-bold uppercase tracking-widest ${event.vibesTextDark ? 'text-slate-900' : 'on-dark-bg text-white'}`}>{event.vibes}</span>
          </div>
        )}
        {isAdmin && (
          <div className="absolute top-3 right-3 flex gap-1">
            <button onClick={onEdit} className="bg-black/50 text-primary p-1.5 rounded-lg hover:bg-primary/20 transition">
              <span className="material-symbols-outlined" style={{fontSize:'14px'}}>edit</span>
            </button>
            <button onClick={()=>onDelete(event.id)} className="bg-black/50 text-red-400 p-1.5 rounded-lg hover:bg-red-900/30 transition">
              <span className="material-symbols-outlined" style={{fontSize:'14px'}}>close</span>
            </button>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-5 space-y-3">
        <div>
          {event.eventType && <p className="text-primary/70 text-xs font-bold uppercase tracking-widest mb-1">{event.eventType}</p>}
          <h3 className="text-white text-xl font-bold">{event.name}</h3>
        </div>
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-slate-400">
            <span className="material-symbols-outlined text-primary/70" style={{fontSize:'16px'}}>schedule</span>
            <span className="text-sm">
              {new Date(event.date+'T00:00:00').toLocaleDateString('en-US',{month:'short',day:'numeric'})} at {event.time}{event.endTime ? ` – ${event.endTime}` : ''}
            </span>
          </div>
          <div className="flex items-center gap-2 text-slate-400">
            <span className="material-symbols-outlined text-primary/70" style={{fontSize:'16px'}}>location_on</span>
            {event.venueCoords ? (
              <a href={buildGoogleMapsUrl(event.venueCoords)} target="_blank" rel="noopener noreferrer"
                className="text-sm text-primary hover:underline" onClick={e=>e.stopPropagation()}>
                {event.venue} ↗
              </a>
            ) : (
              <span className="text-sm">{event.venue}</span>
            )}
          </div>
          {event.dressCode && (
            <div className="flex items-center gap-2 text-slate-400">
              <span className="material-symbols-outlined text-primary/70" style={{fontSize:'16px'}}>checkroom</span>
              <span className="text-sm">Dress Code: <span className="text-primary font-medium">{event.dressCode}</span></span>
            </div>
          )}
        </div>

        {(event.description || event.subEvents) && (
          <>
            <button onClick={()=>setExpanded(!expanded)} className="text-sm text-primary font-semibold flex items-center gap-1">
              {expanded ? 'Show less' : 'Show more'}
              <span className="material-symbols-outlined" style={{fontSize:'16px'}}>{expanded?'expand_less':'expand_more'}</span>
            </button>
            {expanded && (
              <div className="pt-2 border-t border-primary/10 space-y-2">
                {event.description && <p className="text-sm text-slate-300">{event.description}</p>}
                {event.subEvents && <p className="text-sm text-slate-400">{event.subEvents}</p>}
              </div>
            )}
          </>
        )}

        {event.venueCoords && (
          <a href={buildGoogleMapsUrl(event.venueCoords)} target="_blank" rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 w-full h-10 bg-primary text-white text-sm font-bold rounded-xl shadow-md shadow-primary/20">
            <span className="material-symbols-outlined" style={{fontSize:'16px'}}>map</span>
            View Map
          </a>
        )}
      </div>
    </div>
  );
}

function EventForm({ event, onSave, onCancel }) {
  const [form, setForm] = useState({
    name: event?.name||'', eventType: event?.eventType||'', date: event?.date||'', time: event?.time||'', endTime: event?.endTime||'',
    venue: event?.venue||'', venueCoords: event?.venueCoords||'',
    dressCode: event?.dressCode||'', vibes: event?.vibes||'', vibesTextDark: event?.vibesTextDark||false, imageUrl: event?.imageUrl||'',
    color1: event?.color1||'', color2: event?.color2||'', description: event?.description||'', subEvents: event?.subEvents||'',
  });

  const inputCls = "w-full px-3 py-2 bg-white/5 border border-primary/20 rounded-xl text-sm text-white placeholder-slate-600 focus:outline-none focus:border-primary";

  return (
    <form onSubmit={e=>{e.preventDefault();onSave(form);}} className="bg-primary/5 border border-primary/20 rounded-xl p-4 space-y-3">
      <h3 className="font-bold text-slate-100">{event ? 'Edit Event' : 'Add New Event'}</h3>
      <input type="text" placeholder="Event Name" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} className={inputCls} required />
      <input type="text" placeholder="Event Type (e.g. Haldi, Sangeet, Reception)" value={form.eventType} onChange={e=>setForm({...form,eventType:e.target.value})} className={inputCls} />
      <div className="grid grid-cols-2 gap-2">
        <input type="date" value={form.date} onChange={e=>setForm({...form,date:e.target.value})} className={inputCls} required />
        <input type="time" value={form.time} onChange={e=>setForm({...form,time:e.target.value})} className={inputCls} required />
      </div>
      <div className="grid grid-cols-2 gap-2 items-center">
        <input type="time" placeholder="End time (optional)" value={form.endTime} onChange={e=>setForm({...form,endTime:e.target.value})} className={inputCls} />
        <span className="text-xs text-slate-500 px-1">End time (for &quot;next event&quot; logic)</span>
      </div>
      <input type="text" placeholder="Venue" value={form.venue} onChange={e=>setForm({...form,venue:e.target.value})} className={inputCls} required />
      <div>
        <input type="text" placeholder="Google Maps link or coordinates (lat,lng)" value={form.venueCoords} onChange={e=>setForm({...form,venueCoords:e.target.value})} className={inputCls} />
        <p className="text-xs text-slate-500 mt-1 px-1">Paste a Maps link, place URL, or lat,lng</p>
      </div>
      <input type="text" placeholder="Dress Code" value={form.dressCode} onChange={e=>setForm({...form,dressCode:e.target.value})} className={inputCls} />
      <input type="text" placeholder="Vibes (e.g. Golden Hour · Garden Party)" value={form.vibes} onChange={e=>setForm({...form,vibes:e.target.value})} className={inputCls} />
      {form.vibes && (
        <label className="flex items-center gap-2 cursor-pointer select-none px-1">
          <input type="checkbox" checked={!!form.vibesTextDark} onChange={e=>setForm({...form,vibesTextDark:e.target.checked})} className="rounded" />
          <span className="text-xs text-slate-400">Dark text overlay <span className="text-slate-600">(use when photo background is light)</span></span>
        </label>
      )}
      <div>
        <input type="url" placeholder="Photo URL (optional — replaces colour gradient)" value={form.imageUrl} onChange={e=>setForm({...form,imageUrl:e.target.value})} className={inputCls} />
        <p className="text-xs text-slate-500 mt-1 px-1">Paste a direct image URL to show a photo at the top of this card</p>
      </div>
      <div className="space-y-2">
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <input type="checkbox" checked={!!form.color1} onChange={e => {
            if (e.target.checked) setForm({...form, color1: '#930df2', color2: '#7c3aed'});
            else setForm({...form, color1: '', color2: ''});
          }} className="rounded" />
          <span className="text-sm text-slate-400">Custom event colors</span>
        </label>
        {form.color1 && (
          <div className="grid grid-cols-2 gap-2">
            <div className="flex items-center gap-3 bg-white/5 border border-primary/20 rounded-xl px-3 py-2">
              <input type="color" value={form.color1} onChange={e=>setForm({...form,color1:e.target.value})}
                className="w-7 h-7 rounded cursor-pointer border-0 bg-transparent" style={{padding:0}} />
              <span className="text-sm text-slate-400">Color 1</span>
            </div>
            <div className="flex items-center gap-3 bg-white/5 border border-primary/20 rounded-xl px-3 py-2">
              <input type="color" value={form.color2 || form.color1} onChange={e=>setForm({...form,color2:e.target.value})}
                className="w-7 h-7 rounded cursor-pointer border-0 bg-transparent" style={{padding:0}} />
              <span className="text-sm text-slate-400">Color 2</span>
            </div>
          </div>
        )}
      </div>
      <textarea placeholder="Description (optional)" value={form.description} onChange={e=>setForm({...form,description:e.target.value})} className={inputCls} rows="2" />
      <textarea placeholder="Sub-events or notes" value={form.subEvents} onChange={e=>setForm({...form,subEvents:e.target.value})} className={inputCls} rows="2" />
      <div className="flex gap-2">
        <button type="submit" className="flex-1 bg-primary text-white py-2 rounded-xl font-bold text-sm hover:opacity-90">
          {event ? 'Save Changes' : 'Add Event'}
        </button>
        <button type="button" onClick={onCancel} className="px-4 bg-white/5 border border-white/10 text-slate-300 py-2 rounded-xl font-bold text-sm">
          Cancel
        </button>
      </div>
    </form>
  );
}

// ─── Games View (Hub + Sub-views) ────────────────────────────────────────────
function GamesView({ guestName, isAdmin }) {
  const tenant = useTenant();
  const features = tenant.features || {};
  const [activeGame, setActiveGame] = useState(null);
  const [userPoints, setUserPoints] = useState(0);
  const [hiveActive, setHiveActive] = useState(false);
  const [predictionCount, setPredictionCount] = useState(0);
  const [triviaCount, setTriviaCount] = useState(0);
  const [saplinksCompleted, setSaplinksCompleted] = useState(false);
  const [saplinksEnabled, setSaplinksEnabled] = useState(true);
  const [saplinksMaxPts, setSaplinksMaxPts] = useState(10);

  useEffect(() => { if (!activeGame) { loadStats(); } }, [activeGame]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadStats = async () => {
    try {
      const lb = await storage.get('leaderboard', true);
      if (lb) setUserPoints((JSON.parse(lb.value))[guestName] || 0);
    } catch (e) {}
    try {
      const hd = await storage.get('hive-challenges', true);
      if (hd) {
        const challenges = JSON.parse(hd.value);
        setHiveActive(challenges.some(c => !c.revealed));
      }
    } catch (e) {}
    try {
      const gd = await storage.get('wedding-games', true);
      if (gd) setPredictionCount(JSON.parse(gd.value).filter(g=>!g.revealed).length);
    } catch (e) {}
    try {
      const td = await storage.get('couples-trivia', true);
      if (td) setTriviaCount(JSON.parse(td.value).filter(q=>q.active !== false).length);
    } catch (e) {}
    try {
      const puzzleD = await storage.get('sap-links-puzzle', true);
      const parsedPuzzle = puzzleD ? JSON.parse(puzzleD.value) : null;
      const puzzleId = parsedPuzzle ? parsedPuzzle.id : 'chain-reactions';
      setSaplinksEnabled(parsedPuzzle ? parsedPuzzle.enabled !== false : true);
      setSaplinksMaxPts(parsedPuzzle?.points || 10);
      const compD = await storage.get(`sap-links-completions-${puzzleId}`, true);
      const completions = compD ? JSON.parse(compD.value) : {};
      setSaplinksCompleted(!!completions[guestName]);
    } catch (e) {}
  };

  if (activeGame === 'predictions') return <PredictionsGame guestName={guestName} isAdmin={isAdmin} onBack={()=>setActiveGame(null)} />;
  if (activeGame === 'saplinks')    return <SapLinksGame guestName={guestName} isAdmin={isAdmin} onBack={()=>setActiveGame(null)} />;
  if (activeGame === 'hive')        return <HiveGame guestName={guestName} isAdmin={isAdmin} onBack={()=>setActiveGame(null)} />;
  if (activeGame === 'trivia')      return <CouplesTrivia guestName={guestName} isAdmin={isAdmin} onBack={()=>setActiveGame(null)} />;

  return (
    <div>
      {/* Header */}
      <div className="px-6 pt-6 pb-4">
        <h2 className="text-3xl font-bold text-white"><span className="text-primary">{tenant.appName}</span></h2>
        <p className="text-slate-500 text-sm font-medium uppercase tracking-wider">Games Hub</p>
      </div>

      {/* Rank banner */}
      <div className="mx-6 mb-6 bg-primary/10 border border-primary/20 rounded-xl p-4">
        <div className="flex items-center gap-3">
          <span className="material-symbols-outlined text-primary">emoji_events</span>
          <div>
            <p className="text-xs uppercase tracking-wider font-bold text-primary/70">Your Points</p>
            <p className="text-lg font-bold text-white">{guestName}</p>
          </div>
          <div className="ml-auto text-right">
            <p className="text-2xl font-black text-primary">{userPoints.toLocaleString()}</p>
            <p className="text-[10px] uppercase font-bold text-slate-500">Points</p>
          </div>
        </div>
      </div>

      {/* Game Cards */}
      <div className="px-6 space-y-5 pb-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-slate-900 dark:text-white">Wedding Games</h3>
        </div>

        {/* Predictions — full-bleed primary colour card */}
        <div className="relative rounded-xl overflow-hidden cursor-pointer shadow-md" onClick={()=>setActiveGame('predictions')}>
          <div className="absolute inset-0 bg-primary" />
          <div className="absolute inset-0" style={{background:'linear-gradient(135deg, transparent 30%, rgba(0,0,0,0.40) 100%)'}} />
          <span className="material-symbols-outlined absolute -right-6 -top-4 pointer-events-none select-none" style={{fontSize:'200px', color:'rgba(255,255,255,0.10)'}}>trending_up</span>
          <div className="relative z-10 p-5 min-h-[175px] flex flex-col">
            <div className="flex justify-between items-start">
              <div className="text-[10px] font-bold px-3 py-1 rounded-full on-dark-bg text-white" style={{background:'rgba(255,255,255,0.18)', border:'1px solid rgba(255,255,255,0.28)'}}>
                {predictionCount > 0 ? '● LIVE NOW' : 'OPEN'}
              </div>
              <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{background:'rgba(255,255,255,0.18)', border:'1px solid rgba(255,255,255,0.25)'}}>
                <span className="material-symbols-outlined on-dark-bg text-white" style={{fontSize:'18px'}}>play_arrow</span>
              </div>
            </div>
            <div className="mt-auto pt-4">
              <h4 className="on-dark-bg text-white text-xl font-extrabold tracking-tight">{tenant.games?.predictions?.title || 'Wedding Predictions'}</h4>
              <p className="text-sm mt-1" style={{color:'rgba(255,255,255,0.65)'}}>Guess what happens at the wedding!</p>
              <div className="mt-3 flex gap-2">
                <span className="on-dark-bg text-white text-[10px] font-bold px-3 py-1 rounded-full" style={{background:'rgba(255,255,255,0.15)', border:'1px solid rgba(255,255,255,0.22)'}}>+10 PTS</span>
                {predictionCount > 0 && <span className="on-dark-bg text-white text-[10px] font-bold px-3 py-1 rounded-full" style={{background:'rgba(255,255,255,0.10)', border:'1px solid rgba(255,255,255,0.18)'}}>{predictionCount} ACTIVE</span>}
              </div>
            </div>
          </div>
        </div>

        {/* Sap-Links Puzzle — deep forest green card */}
        {features.sapLinks !== false && <div className={`relative rounded-xl overflow-hidden cursor-pointer shadow-md transition-opacity ${!saplinksEnabled ? 'opacity-60' : ''}`} onClick={()=>setActiveGame('saplinks')}>
          <div className="absolute inset-0" style={{background:'linear-gradient(135deg, #1e4530 0%, #0f2318 100%)'}} />
          <div className="absolute inset-0" style={{background:'linear-gradient(135deg, transparent 40%, rgba(0,0,0,0.35) 100%)'}} />
          <span className="material-symbols-outlined absolute -left-5 -bottom-6 pointer-events-none select-none" style={{fontSize:'170px', color:'rgba(255,255,255,0.10)'}}>hub</span>
          <div className="relative z-10 p-5 min-h-[175px] flex flex-col">
            <div className="flex justify-between items-start">
              <div>
                {saplinksCompleted ? (
                  <div className="text-[10px] font-bold px-3 py-1 rounded-full flex items-center gap-1 on-dark-bg text-white" style={{background:'rgba(74,222,128,0.25)', border:'1px solid rgba(74,222,128,0.40)'}}>
                    <span className="material-symbols-outlined fill-icon" style={{fontSize:'11px'}}>check_circle</span>SOLVED
                  </div>
                ) : !saplinksEnabled ? (
                  <div className="text-[10px] font-bold px-3 py-1 rounded-full on-dark-bg text-white" style={{background:'rgba(255,255,255,0.15)', border:'1px solid rgba(255,255,255,0.22)'}}>COMING SOON</div>
                ) : (
                  <div className="text-[10px] font-bold px-3 py-1 rounded-full on-dark-bg text-white" style={{background:'rgba(255,255,255,0.18)', border:'1px solid rgba(255,255,255,0.28)'}}>DAILY PUZZLE</div>
                )}
              </div>
              <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{background:'rgba(255,255,255,0.18)', border:'1px solid rgba(255,255,255,0.25)'}}>
                <span className="material-symbols-outlined on-dark-bg text-white" style={{fontSize:'18px'}}>extension</span>
              </div>
            </div>
            <div className="mt-auto pt-4">
              <h4 className="on-dark-bg text-white text-xl font-extrabold tracking-tight">Sap-Links Puzzle</h4>
              <p className="text-sm mt-1" style={{color:'rgba(255,255,255,0.65)'}}>Complete the puzzle to link root to leaf.</p>
              <div className="mt-3 flex gap-2">
                <span className="on-dark-bg text-white text-[10px] font-bold px-3 py-1 rounded-full" style={{background:'rgba(255,255,255,0.15)', border:'1px solid rgba(255,255,255,0.22)'}}>UP TO {saplinksMaxPts} PTS</span>
                {saplinksCompleted && <span className="text-[10px] font-bold px-3 py-1 rounded-full" style={{color:'rgb(74,222,128)', background:'rgba(74,222,128,0.15)', border:'1px solid rgba(74,222,128,0.30)'}}>COMPLETED</span>}
              </div>
            </div>
          </div>
        </div>}

        {/* Search the Hive */}
        <div className="relative rounded-xl overflow-hidden border border-amber-300/60 dark:border-amber-500/20 cursor-pointer bg-amber-50 dark:bg-transparent" onClick={()=>setActiveGame('hive')}>
          <div className="h-36 bg-gradient-to-br from-amber-200/80 via-amber-100/60 to-transparent dark:from-amber-900/40 dark:via-transparent dark:to-background-dark relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-t from-transparent to-transparent dark:from-background-dark/80 dark:to-transparent" />
            <span className="material-symbols-outlined absolute -right-4 -bottom-4 text-amber-500 pointer-events-none select-none" style={{fontSize:'160px', opacity:0.25}}>emoji_nature</span>
            {hiveActive && (
              <div className="absolute top-3 right-3 bg-amber-500 text-black text-xs font-bold px-2 py-1 rounded-full">LIVE NOW</div>
            )}
          </div>
          <div className="p-5 -mt-10 relative z-10 bg-transparent dark:bg-transparent">
            <div className="flex justify-between items-end">
              <div>
                <h4 className="text-xl font-extrabold text-slate-900 dark:text-white tracking-tight">Search the Hive</h4>
                <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">Work together to find today's answer!</p>
              </div>
              <div className="bg-amber-500 text-black w-12 h-12 rounded-xl flex items-center justify-center shadow-lg shadow-amber-500/20">
                <span className="material-symbols-outlined fill-icon">emoji_nature</span>
              </div>
            </div>
            <div className="mt-3 flex gap-2">
              <span className="bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400 px-3 py-1 rounded-full text-[10px] font-bold">UP TO +10 PTS</span>
              {hiveActive && <span className="bg-amber-100 text-amber-700 border border-amber-300 dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-500/20 px-3 py-1 rounded-full text-[10px] font-bold">CHALLENGE ACTIVE</span>}
            </div>
          </div>
        </div>

        {/* Couples Trivia — hidden when feature disabled */}
        {features.trivia !== false && (
          <div className="relative rounded-xl overflow-hidden border border-rose-300/40 dark:border-rose-500/20 cursor-pointer bg-rose-50 dark:bg-transparent" onClick={()=>setActiveGame('trivia')}>
            <div className="h-36 bg-gradient-to-br from-rose-200/70 via-rose-100/50 to-transparent dark:from-rose-900/40 dark:via-transparent dark:to-background-dark relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-t from-transparent to-transparent dark:from-background-dark/80 dark:to-transparent" />
              <span className="material-symbols-outlined absolute -left-4 -bottom-6 text-rose-400 pointer-events-none select-none" style={{fontSize:'160px', opacity:0.22}}>favorite</span>
            </div>
            <div className="p-5 -mt-10 relative z-10 bg-transparent dark:bg-transparent">
              <div className="flex justify-between items-end">
                <div>
                  <h4 className="text-xl font-extrabold text-slate-900 dark:text-white tracking-tight">Couples Trivia</h4>
                  <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">How well do you know {tenant.coupleNames}?</p>
                </div>
                <div className="bg-rose-500 text-white w-12 h-12 rounded-xl flex items-center justify-center shadow-lg shadow-rose-500/20">
                  <span className="material-symbols-outlined fill-icon">quiz</span>
                </div>
              </div>
              <div className="mt-3 flex gap-2">
                <span className="bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-400 px-3 py-1 rounded-full text-[10px] font-bold">+10 PTS / QUESTION</span>
                {triviaCount > 0 && <span className="bg-rose-500 text-white px-3 py-1 rounded-full text-[10px] font-bold">● LIVE NOW</span>}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Predictions Game ─────────────────────────────────────────────────────────
function PredictionsGame({ guestName, isAdmin, onBack }) {
  const tenant = useTenant();
  const eventTags = tenant.eventTags || EVENT_TAGS;
  const [games, setGames] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [tab, setTab] = useState('open');

  useEffect(() => { loadGames(); }, []);

  const loadGames = async () => {
    try { const d = await storage.get('wedding-games', true); if (d) setGames(JSON.parse(d.value)); }
    catch (e) { setGames([]); }
  };

  const saveGames = async (g) => {
    try { await storage.set('wedding-games', JSON.stringify(g), true); setGames(g); }
    catch (e) { console.error(e); }
  };

  const addGame = d => saveGames([...games, { id:Date.now().toString(), ...d, predictions:{}, revealed:false, createdAt:new Date().toISOString() }]).then(()=>setShowAddForm(false));

  const submitPrediction = async (gameId, prediction) => {
    await saveGames(games.map(g => g.id===gameId ? {...g, predictions:{...g.predictions,[guestName]:prediction}} : g));
  };

  const revealAnswer = async (gameId, correctAnswer) => {
    const game = games.find(g=>g.id===gameId);
    if (!game) return;
    const correct = Object.entries(game.predictions).filter(([,p])=>p===correctAnswer).map(([name])=>name);
    for (const name of correct) {
      try { await awardPoints(name, 10); } catch(e) {}
    }
    await saveGames(games.map(g => g.id===gameId ? {...g, revealed:true, correctAnswer} : g));
  };

  const deleteGame = id => { if(window.confirm('Delete this game?')) saveGames(games.filter(g=>g.id!==id)); };

  const past = games.filter(g=>g.revealed).sort((a,b)=>new Date(b.createdAt)-new Date(a.createdAt));

  // Group open predictions by event tag, ordered by event order
  const openGames = games.filter(g=>!g.revealed);
  const isExpiredGame = g => g.expiresAt && new Date(g.expiresAt) < new Date();
  const hasVotedGame = g => !!g.predictions[guestName];
  const groupMap = {};
  eventTags.forEach(tag => { groupMap[tag] = []; });
  openGames.forEach(g => {
    const tag = g.eventTag || 'General';
    if (!groupMap[tag]) groupMap[tag] = [];
    groupMap[tag].push(g);
  });
  const sortGroup = gs => [...gs].sort((a, b) => {
    const aExp = isExpiredGame(a), bExp = isExpiredGame(b);
    if (aExp !== bExp) return aExp ? 1 : -1;
    const aVoted = hasVotedGame(a), bVoted = hasVotedGame(b);
    if (!aExp && aVoted !== bVoted) return aVoted ? 1 : -1;
    return new Date(b.createdAt) - new Date(a.createdAt);
  });
  const activeGroups = [], expiredGroups = [];
  eventTags.forEach(tag => {
    const group = groupMap[tag];
    if (!group || group.length === 0) return;
    const allExpired = group.every(isExpiredGame);
    (allExpired ? expiredGroups : activeGroups).push({ tag, games: sortGroup(group) });
  });
  const orderedGroups = [...activeGroups, ...expiredGroups];
  const open = openGames; // keep for empty-state count

  return (
    <div className="min-h-full">
      {/* Sub-header */}
      <div className="flex items-center bg-background-dark/80 backdrop-blur-md px-4 py-3 border-b border-primary/10">
        <button onClick={onBack} className="flex size-10 items-center justify-center rounded-full bg-primary/10 text-primary mr-3">
          <span className="material-symbols-outlined">arrow_back</span>
        </button>
        <h2 className="text-white font-bold text-lg flex-1">The Prediction</h2>
        {isAdmin && (
          <button onClick={()=>setShowAddForm(!showAddForm)} className="bg-primary/20 text-primary p-2 rounded-xl border border-primary/20">
            <span className="material-symbols-outlined" style={{fontSize:'20px'}}>add</span>
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex border-b border-primary/10 px-4">
        {[['open','Open Predictions'],['past','Past Predictions']].map(([k,l]) => (
          <button key={k} onClick={()=>setTab(k)}
            className={`flex-1 py-3 text-sm font-bold border-b-2 transition ${tab===k?'border-primary text-primary':'border-transparent text-slate-500'}`}>
            {l}
          </button>
        ))}
      </div>

      <div className="p-4 space-y-5">
        {tab==='open' && (
          <>
            <div className="flex items-center justify-between">
              <h3 className="text-white text-lg font-bold">Open Predictions</h3>
              {open.length > 0 && <span className="bg-primary/20 text-primary text-xs font-bold px-2 py-1 rounded-full">{open.length} Live</span>}
            </div>
            {showAddForm && <GameForm onSave={addGame} onCancel={()=>setShowAddForm(false)} />}
            {open.length === 0 ? (
              <div className="text-center py-12">
                <span className="material-symbols-outlined text-primary/30" style={{fontSize:'48px'}}>casino</span>
                <p className="text-slate-400 mt-3">No active predictions</p>
                {isAdmin && <p className="text-primary/50 text-sm mt-1">Tap + to add a prediction game</p>}
              </div>
            ) : (
              <div className="space-y-8">
                {orderedGroups.map(({ tag, games: groupGames }) => {
                  const allExpired = groupGames.every(isExpiredGame);
                  return (
                    <div key={tag}>
                      <div className="flex items-center gap-3 mb-4">
                        <div className="h-px flex-1 bg-primary/20" />
                        <span className={`text-xs font-bold uppercase tracking-widest ${allExpired ? 'text-slate-600' : 'text-primary/60'}`}>{tag}</span>
                        {allExpired && <span className="material-symbols-outlined text-slate-600" style={{fontSize:'13px'}}>lock</span>}
                        <div className="h-px flex-1 bg-primary/20" />
                      </div>
                      <div className="space-y-5">
                        {groupGames.map(g => <GameCard key={g.id} game={g} guestName={guestName} isAdmin={isAdmin} onSubmit={submitPrediction} onReveal={revealAnswer} onDelete={deleteGame} />)}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
        {tab==='past' && (
          <>
            <h3 className="text-white text-lg font-bold">Past Predictions</h3>
            {past.length === 0 ? (
              <div className="text-center py-12">
                <span className="material-symbols-outlined text-primary/30" style={{fontSize:'48px'}}>history</span>
                <p className="text-slate-400 mt-3">No completed predictions yet</p>
              </div>
            ) : (
              past.map(g => <GameCard key={g.id} game={g} guestName={guestName} isAdmin={isAdmin} onSubmit={submitPrediction} onReveal={revealAnswer} onDelete={deleteGame} />)
            )}
          </>
        )}
      </div>
    </div>
  );
}

function formatExpiry(expiresAt) {
  if (!expiresAt) return null;
  const exp = new Date(expiresAt);
  const now = new Date();
  const diffMs = exp - now;
  if (diffMs <= 0) return { label: 'Voting closed', closed: true, urgent: false };
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  if (diffMins < 60) return { label: `Closes in ${diffMins}m`, closed: false, urgent: true };
  if (diffHours < 4) return { label: `Closes in ${diffHours}h ${diffMins % 60}m`, closed: false, urgent: true };
  const isToday = exp.toDateString() === now.toDateString();
  const timeStr = exp.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  if (isToday) return { label: `Closes today at ${timeStr}`, closed: false, urgent: false };
  const dateStr = exp.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  return { label: `Closes ${dateStr} at ${timeStr}`, closed: false, urgent: false };
}

function GameCard({ game, guestName, isAdmin, onSubmit, onReveal, onDelete }) {
  const [selected, setSelected] = useState('');
  const [showReveal, setShowReveal] = useState(false);
  const [revealAns, setRevealAns] = useState('');

  const userPred = game.predictions[guestName];
  const hasVoted = !!userPred;
  const total = Object.keys(game.predictions).length;
  const expiryInfo = formatExpiry(game.expiresAt);
  const isGameExpired = expiryInfo?.closed ?? false;

  return (
    <div className="rounded-xl bg-white/5 border border-primary/10 overflow-hidden">
      {/* Card header */}
      <div className="h-28 bg-gradient-to-br from-primary/30 to-background-dark relative">
        <div className="absolute inset-0 bg-gradient-to-t from-background-dark/70 to-transparent" />
        <div className="absolute bottom-3 left-4 flex items-center gap-2">
          {game.eventTag && (
            <span className={`text-[10px] font-bold px-2 py-1 rounded uppercase tracking-widest ${
              game.eventTag === 'General'
                ? 'bg-white/15 text-white/80'
                : 'bg-primary/80 text-white'
            }`}>
              {game.eventTag}
            </span>
          )}
          <span className={`text-[10px] font-bold px-2 py-1 rounded uppercase tracking-widest ${
            game.revealed ? 'bg-green-900/60 text-green-400' : 'bg-white/10 text-slate-300'
          }`}>
            {game.revealed ? 'Completed' : 'Active'}
          </span>
        </div>
      </div>

      <div className="p-4">
        <div className="flex justify-between items-start mb-3">
          <h4 className="text-white font-bold text-base leading-tight flex-1 pr-2">{game.question}</h4>
          {isAdmin && !game.revealed && (
            <button onClick={()=>onDelete(game.id)} className="text-slate-500 hover:text-red-400 p-1 transition">
              <span className="material-symbols-outlined" style={{fontSize:'18px'}}>close</span>
            </button>
          )}
        </div>
        <p className="text-slate-500 text-xs mb-1">{total} prediction{total!==1?'s':''} submitted</p>
        {expiryInfo && (
          <div className={`flex items-center gap-1.5 mb-4 ${expiryInfo.closed ? 'text-slate-600' : expiryInfo.urgent ? 'text-amber-400' : 'text-slate-500'}`}>
            <span className="material-symbols-outlined" style={{fontSize:'13px'}}>{expiryInfo.closed ? 'lock' : 'schedule'}</span>
            <span className="text-xs font-medium">{expiryInfo.label}</span>
          </div>
        )}
        {!expiryInfo && <div className="mb-4" />}

        {/* Admin view – unrevealed */}
        {isAdmin && !game.revealed && (
          <div className="space-y-3">
            <div className="bg-primary/10 border border-primary/20 rounded-xl p-4">
              <p className="text-primary text-sm font-bold mb-3">Admin — {total} vote{total!==1?'s':''}</p>
              {!showReveal ? (
                <button onClick={()=>setShowReveal(true)} disabled={total===0}
                  className="w-full bg-primary text-white py-2 rounded-xl font-bold text-sm disabled:opacity-40 disabled:cursor-not-allowed">
                  {total===0?'Waiting for votes…':'Reveal Answer'}
                </button>
              ) : (
                <div className="space-y-2">
                  <select value={revealAns} onChange={e=>setRevealAns(e.target.value)}
                    className="w-full px-3 py-2 bg-white/5 border border-primary/20 rounded-xl text-sm text-white">
                    <option value="">Select correct answer…</option>
                    {game.options.map((o,i) => <option key={i} value={o}>{o}</option>)}
                  </select>
                  <div className="flex gap-2">
                    <button onClick={()=>{ if(revealAns){onReveal(game.id,revealAns);setShowReveal(false);} }} disabled={!revealAns}
                      className="flex-1 bg-primary text-white py-2 rounded-xl font-bold text-sm disabled:opacity-40">
                      Confirm & Award Points
                    </button>
                    <button onClick={()=>setShowReveal(false)} className="px-4 bg-white/5 text-slate-300 py-2 rounded-xl text-sm">Cancel</button>
                  </div>
                </div>
              )}
            </div>
            <div className="space-y-2">
              {game.options.map(opt => {
                const votes = Object.values(game.predictions).filter(p=>p===opt).length;
                const pct = total > 0 ? Math.round(votes/total*100) : 0;
                return (
                  <div key={opt}>
                    <div className="flex justify-between text-xs mb-1"><span className="text-slate-300">{opt}</span><span className="text-slate-500">{votes} ({pct}%)</span></div>
                    <div className="bg-white/10 rounded-full h-2"><div className="bg-primary h-full rounded-full" style={{width:`${pct}%`}} /></div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Guest – not voted */}
        {!isAdmin && !game.revealed && !hasVoted && (
          isGameExpired ? (
            <div className="bg-white/5 border border-white/10 rounded-xl p-4 flex items-center gap-3">
              <span className="material-symbols-outlined text-slate-500">lock</span>
              <div>
                <p className="text-slate-400 text-sm font-bold">Voting closed</p>
                <p className="text-slate-600 text-xs mt-0.5">Results will be revealed soon</p>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="space-y-2">
                {game.options.map((opt,i) => (
                  <button key={i} onClick={()=>setSelected(opt)}
                    className={`flex items-center justify-between w-full p-3 rounded-xl border text-left transition ${
                      selected===opt ? 'border-primary bg-primary/10' : 'border-primary/20 bg-white/5 hover:border-primary/40'
                    }`}>
                    <span className={`text-sm ${selected===opt?'text-white font-bold':'text-slate-300'}`}>{opt}</span>
                    <span className="material-symbols-outlined text-primary" style={{fontSize:'18px'}}>
                      {selected===opt ? 'check_circle' : 'radio_button_unchecked'}
                    </span>
                  </button>
                ))}
              </div>
              <button onClick={()=>selected&&onSubmit(game.id,selected)} disabled={!selected}
                className="w-full bg-primary text-white font-bold py-3 rounded-xl shadow-lg shadow-primary/20 disabled:opacity-40 disabled:cursor-not-allowed">
                Submit Vote
              </button>
            </div>
          )
        )}

        {/* Guest – voted, not revealed */}
        {!isAdmin && !game.revealed && hasVoted && (
          <div className="bg-primary/10 border border-primary/20 rounded-xl p-4 flex items-center gap-3">
            <span className="material-symbols-outlined text-primary">check_circle</span>
            <div>
              <p className="text-white text-sm font-bold">Your prediction: {userPred}</p>
              <p className="text-primary/60 text-xs mt-0.5">Waiting for results…</p>
            </div>
          </div>
        )}

        {/* Revealed */}
        {game.revealed && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 mb-2">
              <span className="material-symbols-outlined text-green-400" style={{fontSize:'16px'}}>check_circle</span>
              <span className="text-[10px] font-bold text-green-400 uppercase tracking-widest">Completed</span>
            </div>
            <div className="bg-primary/10 border border-primary/20 rounded-xl p-4">
              <p className="text-primary font-bold text-sm mb-1">✨ Answer: {game.correctAnswer}</p>
              {!isAdmin && userPred && (
                <p className="text-sm text-slate-300">
                  You predicted: {userPred}
                  {userPred===game.correctAnswer
                    ? <span className="ml-2 text-green-400 font-bold">+10 points! 🎉</span>
                    : <span className="ml-2 text-slate-500">Better luck next time</span>}
                </p>
              )}
            </div>
            <div className="space-y-2">
              {game.options.map(opt => {
                const votes = Object.values(game.predictions).filter(p=>p===opt).length;
                const pct = total > 0 ? Math.round(votes/total*100) : 0;
                const isCorrect = opt===game.correctAnswer;
                return (
                  <div key={opt}>
                    <div className="flex justify-between text-xs mb-1">
                      <span className={isCorrect?'text-primary font-bold':'text-slate-400'}>{opt}{isCorrect?' ✓':''}</span>
                      <span className={isCorrect?'text-primary font-bold':'text-slate-500'}>{pct}%</span>
                    </div>
                    <div className="bg-white/10 rounded-full h-2">
                      <div className={`h-full rounded-full ${isCorrect?'bg-primary':'bg-primary/40'}`} style={{width:`${pct}%`}} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const EVENT_TAGS = ['General', 'Welcome Night', 'Haldi', 'Sangeet', 'Wedding', 'Reception'];

function GameForm({ onSave, onCancel }) {
  const tenant = useTenant();
  const eventTags = tenant.eventTags || EVENT_TAGS;
  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState(['','','']);
  const [eventTag, setEventTag] = useState('General');

  const [expiresAt, setExpiresAt] = useState('');

  const handleSubmit = e => {
    e.preventDefault();
    const valid = options.filter(o=>o.trim());
    if (valid.length >= 2) { onSave({ question, options: valid, eventTag, expiresAt: expiresAt || null }); }
  };

  const inputCls = "w-full px-3 py-2 bg-white/5 border border-primary/20 rounded-xl text-sm text-white placeholder-slate-600 focus:outline-none focus:border-primary";

  return (
    <div className="bg-primary/5 border border-primary/20 rounded-xl p-4 space-y-3">
      <h3 className="font-bold text-slate-100">Add Prediction Game</h3>
      <div>
        <label className="text-xs font-bold text-primary/60 uppercase tracking-widest block mb-1.5">Event</label>
        <div className="flex gap-2 flex-wrap">
          {eventTags.map(tag => (
            <button key={tag} type="button" onClick={()=>setEventTag(tag)}
              className={`px-3 py-1.5 rounded-full text-xs font-bold transition border ${
                eventTag===tag
                  ? 'bg-primary text-white border-primary'
                  : 'bg-white/5 text-slate-400 border-primary/20 hover:border-primary/40'
              }`}>
              {tag}
            </button>
          ))}
        </div>
      </div>
      <textarea placeholder="Question (e.g., Who will cry first?)" value={question} onChange={e=>setQuestion(e.target.value)} className={inputCls} rows="2" required />
      <div>
        <label className="text-xs font-bold text-primary/60 uppercase tracking-widest block mb-1.5">Voting closes at (optional)</label>
        <input type="datetime-local" value={expiresAt} onChange={e=>setExpiresAt(e.target.value)} className={inputCls} />
      </div>
      <div className="space-y-2">
        <label className="text-xs font-bold text-primary/60 uppercase tracking-widest">Options (min 2)</label>
        {options.map((o,i) => (
          <input key={i} type="text" placeholder={`Option ${i+1}`} value={o} onChange={e=>{const n=[...options];n[i]=e.target.value;setOptions(n);}} className={inputCls} />
        ))}
        <button type="button" onClick={()=>setOptions([...options,''])} className="text-sm text-primary font-semibold">+ Add option</button>
      </div>
      <div className="flex gap-2">
        <button onClick={handleSubmit} className="flex-1 bg-primary text-white py-2 rounded-xl font-bold text-sm hover:opacity-90">Create Game</button>
        <button onClick={onCancel} className="px-4 bg-white/5 border border-white/10 text-slate-300 py-2 rounded-xl text-sm">Cancel</button>
      </div>
    </div>
  );
}

// ─── Daily Puzzle (placeholder) ───────────────────────────────────────────────
// ─── Sap Links — Default Puzzle ───────────────────────────────────────────────
const SAP_LINKS_DEFAULT_PUZZLE = {
  id: 'chain-reactions',
  title: 'Chain Reactions',
  difficulty: 'medium',
  nodes: [
    { id: 'n1', word: 'RAIN',    given: true,  x: 90,  y: 141 },
    { id: 'n2', word: 'REALITY', given: true,  x: 102, y: 348 },
    { id: 'n3', word: null,      given: false, x: 238, y: 268, firstLetter: 'C' },
    { id: 'n4', word: 'MATE',    given: true,  x: 410, y: 268 },
    { id: 'n5', word: null,      given: false, x: 287, y: 141, firstLetter: 'W' },
    { id: 'n6', word: null,      given: false, x: 453, y: 52,  firstLetter: 'B' },
    { id: 'n7', word: 'BALLOON', given: true,  x: 543, y: 139 },
    { id: 'n8', word: 'NECK',    given: true,  x: 623, y: 53  },
  ],
  edges: [
    { from: 'n1', to: 'n3', phrase: 'rain check' },
    { from: 'n2', to: 'n3', phrase: 'reality check' },
    { from: 'n3', to: 'n4', phrase: 'check mate' },
    { from: 'n1', to: 'n5', phrase: 'rain water' },
    { from: 'n5', to: 'n6', phrase: 'water bottle' },
    { from: 'n6', to: 'n8', phrase: 'bottle neck' },
    { from: 'n5', to: 'n7', phrase: 'water balloon' },
  ],
  solution: { n3: 'CHECK', n5: 'WATER', n6: 'BOTTLE' },
  hints: {
    n3: ['5 letters', 'Something you do to verify'],
    n5: ['5 letters', 'Falls from clouds'],
    n6: ['6 letters', 'Holds liquid'],
  },
};

// ─── Sap Links Game ───────────────────────────────────────────────────────────
function SapLinksGame({ guestName, isAdmin, onBack }) {
  const [puzzle, setPuzzle] = useState(null);
  const [showBuilder, setShowBuilder] = useState(false);
  const [userAnswers, setUserAnswers] = useState({});
  const [selectedNode, setSelectedNode] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [hintsUsed, setHintsUsed] = useState({});
  const [gameWon, setGameWon] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);
  const [pointsAwarded, setPointsAwarded] = useState(false);
  const [completionRank, setCompletionRank] = useState(null);
  const [completionPts, setCompletionPts] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [instructionsOpen, setInstructionsOpen] = useState(true);
  const startTimeRef    = useRef(null); // when the current session started
  const bankedElapsedRef = useRef(0);  // seconds accumulated from previous sessions
  // Mirrors for unmount-cleanup (avoids stale closure over state)
  const puzzleRef      = useRef(null);
  const gameStartedRef = useRef(false);
  const gameWonRef     = useRef(false);

  useEffect(() => { puzzleRef.current = puzzle; },           [puzzle]);
  useEffect(() => { gameStartedRef.current = gameStarted; }, [gameStarted]);
  useEffect(() => { gameWonRef.current = gameWon; },         [gameWon]);

  useEffect(() => { loadPuzzle(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (puzzle) checkPuzzleComplete(); }, [userAnswers]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pause timer on unmount — bank the current session's seconds into localStorage
  useEffect(() => {
    return () => {
      const p = puzzleRef.current;
      if (p && gameStartedRef.current && !gameWonRef.current && startTimeRef.current) {
        const sessionSecs = Math.floor((Date.now() - startTimeRef.current) / 1000);
        const total = bankedElapsedRef.current + sessionSecs;
        localStorage.setItem(`saplinks-elapsed-${p.id}-${guestName}`, total.toString());
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Timer — ticks every second only while game is actively in progress
  useEffect(() => {
    if (!puzzle || !gameStarted || gameWon) return;
    const interval = setInterval(() => {
      if (startTimeRef.current) {
        const sessionSecs = Math.floor((Date.now() - startTimeRef.current) / 1000);
        setElapsed(bankedElapsedRef.current + sessionSecs);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [puzzle, gameStarted, gameWon]);

  const formatTime = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const loadPuzzle = async () => {
    let p;
    try {
      const d = await storage.get('sap-links-puzzle', true);
      p = d ? JSON.parse(d.value) : SAP_LINKS_DEFAULT_PUZZLE;
    } catch(e) { p = SAP_LINKS_DEFAULT_PUZZLE; }
    setPuzzle(p);
    // Restore banked elapsed if the user left mid-puzzle
    const storedElapsed = localStorage.getItem(`saplinks-elapsed-${p.id}-${guestName}`);
    if (storedElapsed !== null) {
      const banked = parseInt(storedElapsed, 10);
      bankedElapsedRef.current = banked;
      startTimeRef.current = Date.now(); // resume counting from now
      setElapsed(banked);
      setGameStarted(true);
    }
  };

  const handleStart = () => {
    bankedElapsedRef.current = 0;
    startTimeRef.current = Date.now();
    localStorage.removeItem(`saplinks-elapsed-${puzzle.id}-${guestName}`);
    setElapsed(0);
    setGameStarted(true);
    setInstructionsOpen(false);
  };

  const getNodeWord = (nodeId) => {
    if (!puzzle) return null;
    const node = puzzle.nodes.find(n => n.id === nodeId);
    if (!node) return null;
    return node.given ? node.word : userAnswers[nodeId];
  };

  const validateEdge = (edge) => {
    const fromWord = getNodeWord(edge.from);
    const toWord = getNodeWord(edge.to);
    if (!fromWord || !toWord) return null;
    return (fromWord + ' ' + toWord).toLowerCase() === edge.phrase.toLowerCase();
  };

  const getEdgeStatus = (edge) => {
    const v = validateEdge(edge);
    if (v === null) return 'pending';
    return v ? 'valid' : 'invalid';
  };

  const checkPuzzleComplete = async () => {
    if (!puzzle) return;
    const allFilled = puzzle.nodes.filter(n => !n.given).every(n => userAnswers[n.id]);
    if (!allFilled) return;
    const allValid = puzzle.edges.every(e => validateEdge(e) === true);
    if (!allValid) return;
    const sessionSecs = startTimeRef.current ? Math.floor((Date.now() - startTimeRef.current) / 1000) : 0;
    const finalElapsed = bankedElapsedRef.current + sessionSecs;
    localStorage.removeItem(`saplinks-elapsed-${puzzle.id}-${guestName}`);
    setGameWon(true);
    try {
      const result = await recordSapLinksCompletion(puzzle.id, guestName, finalElapsed, puzzle.points || 10);
      if (!result.alreadyDone) {
        setPointsAwarded(true);
        setCompletionRank(result.rank);
        setCompletionPts(result.ptsAwarded);
      }
    } catch(e) {}
  };

  const handleNodeClick = (node) => {
    if (node.given) return;
    setSelectedNode(node);
    setInputValue(userAnswers[node.id] || '');
  };

  const handleSubmit = () => {
    if (!selectedNode || !inputValue.trim()) return;
    setUserAnswers({ ...userAnswers, [selectedNode.id]: inputValue.toUpperCase().trim() });
    setSelectedNode(null);
    setInputValue('');
  };

  const showHint = (nodeId) => {
    const level = hintsUsed[nodeId] || 0;
    const hints = puzzle.hints?.[nodeId];
    if (hints && level < hints.length) setHintsUsed({ ...hintsUsed, [nodeId]: level + 1 });
  };

  const resetGame = () => {
    if (puzzle) localStorage.removeItem(`saplinks-elapsed-${puzzle.id}-${guestName}`);
    bankedElapsedRef.current = 0;
    setUserAnswers({});
    setSelectedNode(null);
    setInputValue('');
    setHintsUsed({});
    setGameWon(false);
    setGameStarted(false);
    setPointsAwarded(false);
    setElapsed(0);
    setCompletionRank(null);
    setCompletionPts(null);
    startTimeRef.current = null;
    setInstructionsOpen(true);
  };

  const handleSubmitPuzzle = async (p) => {
    await storage.set('sap-links-puzzle', JSON.stringify(p), true);
    setPuzzle(p);
    resetGame();
    setShowBuilder(false);
  };

  const toggleEnabled = async () => {
    const updated = { ...puzzle, enabled: puzzle.enabled === false ? true : false };
    await storage.set('sap-links-puzzle', JSON.stringify(updated), true);
    setPuzzle(updated);
  };

  if (showBuilder) return <SapLinksPuzzleBuilder onBack={() => setShowBuilder(false)} onSubmitPuzzle={handleSubmitPuzzle} />;

  if (!puzzle) return (
    <div>
      <div className="flex items-center bg-background-dark/80 backdrop-blur-md px-4 py-3 border-b border-primary/10">
        <button onClick={onBack} className="flex size-10 items-center justify-center rounded-full bg-primary/10 text-primary mr-3">
          <span className="material-symbols-outlined">arrow_back</span>
        </button>
        <h2 className="text-white font-bold text-lg">Sap Links</h2>
      </div>
      <div className="flex items-center justify-center py-20 text-primary/50 text-sm">Loading puzzle…</div>
    </div>
  );

  if (!isAdmin && puzzle.enabled === false) return (
    <div className="min-h-full flex flex-col">
      <div className="flex items-center bg-background-dark/80 backdrop-blur-md px-4 py-3 border-b border-primary/10">
        <button onClick={onBack} className="flex size-10 items-center justify-center rounded-full bg-primary/10 text-primary mr-3">
          <span className="material-symbols-outlined">arrow_back</span>
        </button>
        <h2 className="text-white font-bold text-lg flex-1">Sap-Links Puzzle</h2>
      </div>
      <div className="flex-1 flex flex-col items-center justify-center px-8 text-center py-24">
        <span className="material-symbols-outlined text-primary/20" style={{fontSize:'80px'}}>lock_clock</span>
        <h2 className="text-white text-2xl font-extrabold mt-5">Coming Soon</h2>
        <p className="text-slate-400 mt-3 leading-relaxed">The Sap-Links puzzle hasn't dropped yet.<br/>Check back shortly!</p>
        <button onClick={onBack} className="mt-8 bg-primary/20 text-primary border border-primary/20 font-bold px-6 py-3 rounded-xl">
          Back to Games
        </button>
      </div>
    </div>
  );

  const NODE_R = 40;
  const edgeColor = { valid: '#10b981', invalid: '#ef4444', pending: '#d1d5db' };

  return (
    <div className="min-h-full">
      {/* Sub-header */}
      <div className="flex items-center bg-background-dark/80 backdrop-blur-md px-4 py-3 border-b border-primary/10">
        <button onClick={onBack} className="flex size-10 items-center justify-center rounded-full bg-primary/10 text-primary mr-3">
          <span className="material-symbols-outlined">arrow_back</span>
        </button>
        <div className="flex-1 flex items-center gap-3">
          <h2 className="text-white font-bold text-lg leading-tight">Sap Links</h2>
          {gameStarted && !gameWon && (
            <span className="flex items-center gap-1 text-primary/70 text-sm font-bold tabular-nums">
              <span className="material-symbols-outlined" style={{fontSize:'15px'}}>timer</span>
              {formatTime(elapsed)}
            </span>
          )}
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <button onClick={toggleEnabled}
              className={`text-xs font-bold px-3 py-2 rounded-xl border transition ${puzzle.enabled === false ? 'bg-amber-900/30 text-amber-400 border-amber-700/40' : 'bg-green-900/30 text-green-400 border-green-700/40'}`}>
              {puzzle.enabled === false ? 'Disabled' : 'Enabled'}
            </button>
            <button onClick={() => setShowBuilder(true)} className="bg-primary/20 text-primary p-2 rounded-xl border border-primary/20">
              <span className="material-symbols-outlined" style={{fontSize:'20px'}}>edit_square</span>
            </button>
          </div>
        )}
      </div>

      <div className="p-4 space-y-4">
        {/* Puzzle badge */}
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-white font-bold text-xl">{puzzle.title}</h3>
            <p className="text-slate-400 text-sm">Complete the word chains</p>
          </div>
          <span className="bg-primary/20 text-primary px-3 py-1 rounded-full text-xs font-bold capitalize">{puzzle.difficulty}</span>
        </div>

        {/* Instructions */}
        <div className="bg-white/5 border border-primary/10 rounded-xl overflow-hidden">
          <button onClick={() => setInstructionsOpen(!instructionsOpen)} className="w-full px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="material-symbols-outlined text-primary" style={{fontSize:'18px'}}>help_outline</span>
              <span className="text-sm font-bold text-slate-300">How to Play</span>
            </div>
            <span className="material-symbols-outlined text-slate-500" style={{fontSize:'18px'}}>{instructionsOpen ? 'expand_less' : 'expand_more'}</span>
          </button>
          {instructionsOpen && (
            <div className="px-4 pb-4 border-t border-primary/10 pt-3 space-y-1.5">
              {[
                ['radio_button_checked', 'Green circles contain given words'],
                ['radio_button_unchecked', 'Dashed circles need to be filled in'],
                ['east', 'Arrows connect words that must form valid two-word phrases'],
                ['ads_click', 'Tap an empty node to enter a word'],
                ['check_circle', 'Green arrows = valid phrase ✓'],
                ['cancel', 'Red arrows = invalid phrase ✗'],
              ].map(([icon, text]) => (
                <p key={text} className="text-xs text-slate-400 flex items-center gap-2">
                  <span className="material-symbols-outlined text-primary" style={{fontSize:'14px'}}>{icon}</span>{text}
                </p>
              ))}
            </div>
          )}
        </div>

        {/* Start CTA — shown before game begins */}
        {!gameStarted && (
          <div className="bg-primary/10 border border-primary/20 rounded-xl p-6 text-center">
            <span className="material-symbols-outlined text-primary fill-icon" style={{fontSize:'40px'}}>extension</span>
            <h3 className="text-white font-bold text-lg mt-3 mb-1">Ready to solve?</h3>
            <p className="text-slate-400 text-sm mb-5">
              Your timer starts when you press Start.{'\u00a0'}
              <span className="text-primary font-bold">Up to {puzzle.points || 10} pts</span> — first to finish earns the most!
            </p>
            <button onClick={handleStart}
              className="flex items-center gap-2 px-7 py-3 bg-primary text-white rounded-xl font-bold mx-auto text-base shadow-lg shadow-primary/30">
              <span className="material-symbols-outlined fill-icon" style={{fontSize:'20px'}}>play_arrow</span>
              Start Puzzle
            </button>
          </div>
        )}

        {/* Canvas, input panel, win screen and reset — all gated on gameStarted */}
        {gameStarted && (
        <>
        <div className="bg-white/5 border border-primary/10 rounded-xl overflow-x-auto">
          <div className="p-3" style={{minWidth: '716px'}}>
            <svg width="700" height="400">
              <defs>
                <marker id="sl-arrow-valid" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="5" markerHeight="5" orient="auto">
                  <polygon points="0 0, 10 5, 0 10" fill="#10b981" stroke="#059669" strokeWidth="0.5" />
                  <path d="M 0 5 Q -1.5 3.5, -1.5 5 Q -1.5 6.5, 0 5" fill="#10b981" opacity="0.7" />
                </marker>
                <marker id="sl-arrow-invalid" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="5" markerHeight="5" orient="auto">
                  <polygon points="0 0, 10 5, 0 10" fill="#ef4444" stroke="#dc2626" strokeWidth="0.5" />
                  <path d="M 0 5 Q -1.5 3.5, -1.5 5 Q -1.5 6.5, 0 5" fill="#ef4444" opacity="0.7" />
                </marker>
                <marker id="sl-arrow-pending" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="5" markerHeight="5" orient="auto">
                  <polygon points="0 0, 10 5, 0 10" fill="#d1d5db" stroke="#9ca3af" strokeWidth="0.5" />
                  <path d="M 0 5 Q -1.5 3.5, -1.5 5 Q -1.5 6.5, 0 5" fill="#d1d5db" opacity="0.7" />
                </marker>
                {puzzle.nodes.map(node => {
                  const word = getNodeWord(node.id);
                  return (
                    <radialGradient key={node.id} id={`sl-grad-${node.id}`}>
                      <stop offset="0%" stopColor={node.given ? '#10b981' : word ? '#60a5fa' : 'white'} />
                      <stop offset="100%" stopColor={node.given ? '#059669' : word ? '#3b82f6' : '#f9fafb'} />
                    </radialGradient>
                  );
                })}
              </defs>

              {/* Edges */}
              {puzzle.edges.map((edge, idx) => {
                const fromNode = puzzle.nodes.find(n => n.id === edge.from);
                const toNode = puzzle.nodes.find(n => n.id === edge.to);
                if (!fromNode || !toNode) return null;
                const status = getEdgeStatus(edge);
                const dx = toNode.x - fromNode.x, dy = toNode.y - fromNode.y;
                const len = Math.sqrt(dx * dx + dy * dy);
                return (
                  <line key={idx}
                    x1={fromNode.x + (dx / len) * NODE_R}
                    y1={fromNode.y + (dy / len) * NODE_R}
                    x2={toNode.x - (dx / len) * (NODE_R + 10)}
                    y2={toNode.y - (dy / len) * (NODE_R + 10)}
                    stroke={edgeColor[status]} strokeWidth="3"
                    markerEnd={`url(#sl-arrow-${status})`}
                  />
                );
              })}

              {/* Nodes */}
              {puzzle.nodes.map(node => {
                const word = getNodeWord(node.id);
                const isSelected = selectedNode?.id === node.id;
                const hintLevel = hintsUsed[node.id] || 0;
                const displayText = word || (node.firstLetter ? node.firstLetter + '...' : '?');

                const hasTopEdge    = puzzle.edges.some(e => (e.from === node.id && puzzle.nodes.find(n => n.id === e.to)?.y    < node.y) || (e.to === node.id && puzzle.nodes.find(n => n.id === e.from)?.y    < node.y));
                const hasBottomEdge = puzzle.edges.some(e => (e.from === node.id && puzzle.nodes.find(n => n.id === e.to)?.y    > node.y) || (e.to === node.id && puzzle.nodes.find(n => n.id === e.from)?.y    > node.y));
                const hasLeftEdge   = puzzle.edges.some(e => (e.from === node.id && puzzle.nodes.find(n => n.id === e.to)?.x    < node.x) || (e.to === node.id && puzzle.nodes.find(n => n.id === e.from)?.x    < node.x));
                const hasRightEdge  = puzzle.edges.some(e => (e.from === node.id && puzzle.nodes.find(n => n.id === e.to)?.x    > node.x) || (e.to === node.id && puzzle.nodes.find(n => n.id === e.from)?.x    > node.x));

                let leafX = node.x, leafY = node.y - 55, leafRot = 0;
                if (hasTopEdge) {
                  if (!hasBottomEdge)     { leafY = node.y + 55; leafRot = 180; }
                  else if (!hasLeftEdge)  { leafX = node.x - 55; leafY = node.y; leafRot = -90; }
                  else if (!hasRightEdge) { leafX = node.x + 55; leafY = node.y; leafRot = 90; }
                }

                return (
                  <g key={node.id}>
                    {/* Decorative leaf */}
                    <g transform={`translate(${leafX},${leafY}) rotate(${leafRot})`}>
                      <path d="M 0 0 Q -6 -8, 0 -16 Q 6 -8, 0 0"
                        fill={node.given ? '#059669' : word ? '#3b82f6' : '#d1d5db'}
                        opacity={word ? '0.7' : '0.4'} />
                      <line x1="0" y1="0" x2="0" y2="-16"
                        stroke={node.given ? '#047857' : word ? '#2563eb' : '#9ca3af'}
                        strokeWidth="1" opacity={word ? '0.5' : '0.3'} />
                    </g>
                    {/* Circle */}
                    <circle cx={node.x} cy={node.y} r={NODE_R}
                      fill={`url(#sl-grad-${node.id})`}
                      stroke={isSelected ? '#f59e0b' : node.given ? '#047857' : word ? '#2563eb' : '#d1d5db'}
                      strokeWidth={isSelected ? 4 : 2}
                      strokeDasharray={!node.given && !word ? '5,5' : '0'}
                      className={!node.given ? 'cursor-pointer' : ''}
                      onClick={() => handleNodeClick(node)}
                      style={{ filter: isSelected ? 'drop-shadow(0 0 8px rgba(245,158,11,0.4))' : 'none' }}
                    />
                    {/* Inner ring */}
                    {(word || node.given) && (
                      <circle cx={node.x} cy={node.y} r="36" fill="none" stroke="white" strokeWidth="1" opacity="0.3" />
                    )}
                    {/* Label */}
                    <text x={node.x} y={node.y + 5} textAnchor="middle"
                      fill={node.given || word ? 'white' : '#9ca3af'}
                      fontSize="13" fontWeight="bold"
                      style={{ pointerEvents: 'none', userSelect: 'none' }}>
                      {displayText}
                    </text>
                    {/* Hint text */}
                    {hintLevel > 0 && word !== puzzle.solution?.[node.id] && puzzle.hints?.[node.id] && (
                      <text x={node.x} y={node.y + 60} textAnchor="middle" fill="#d97706" fontSize="11" fontWeight="600">
                        {puzzle.hints[node.id][hintLevel - 1]}
                      </text>
                    )}
                  </g>
                );
              })}
            </svg>
          </div>
        </div>

        {/* Input Panel */}
        {selectedNode && (
          <div className="bg-white/5 border border-primary/30 rounded-xl p-4">
            <p className="text-xs font-bold uppercase tracking-wider text-primary/70 mb-3">Fill in the word</p>
            <div className="flex gap-2">
              <input type="text" value={inputValue}
                onChange={e => setInputValue(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSubmit()}
                placeholder="Type word…"
                className="flex-1 px-3 py-2 bg-white/10 border border-primary/30 rounded-xl text-white font-bold uppercase placeholder-slate-600 focus:outline-none focus:border-primary"
                autoFocus
              />
              <button onClick={handleSubmit} className="px-4 bg-primary text-white rounded-xl font-bold text-sm">Submit</button>
              <button onClick={() => setSelectedNode(null)} className="px-3 bg-white/10 border border-white/20 text-slate-300 rounded-xl text-sm">✕</button>
            </div>
            {puzzle.hints?.[selectedNode.id] && (
              <button onClick={() => showHint(selectedNode.id)}
                disabled={(hintsUsed[selectedNode.id] || 0) >= 2}
                className={`mt-3 flex items-center gap-2 px-3 py-1.5 rounded-xl text-sm font-bold transition ${
                  (hintsUsed[selectedNode.id] || 0) >= 2
                    ? 'bg-white/5 text-slate-600 cursor-not-allowed'
                    : 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                }`}>
                <span className="material-symbols-outlined" style={{fontSize:'16px'}}>lightbulb</span>
                Hint ({hintsUsed[selectedNode.id] || 0}/2)
              </button>
            )}
          </div>
        )}

        {/* Win Screen */}
        {gameWon && (
          <div className="bg-primary/10 border border-primary/20 rounded-xl p-6 text-center">
            <span className="material-symbols-outlined text-green-400 fill-icon" style={{fontSize:'48px'}}>check_circle</span>
            <h2 className="text-2xl font-bold text-white mt-3 mb-1">Puzzle Complete!</h2>
            <p className="text-primary/60 text-sm mb-3 flex items-center justify-center gap-1">
              <span className="material-symbols-outlined" style={{fontSize:'14px'}}>timer</span>
              Solved in {formatTime(elapsed)}
            </p>
            {pointsAwarded ? (
              <div className="space-y-1 mb-4">
                <p className="text-green-400 font-bold">+{completionPts} points earned!</p>
                <p className="text-slate-400 text-xs">
                  {completionRank === 1
                    ? 'First to finish — full points! 🏆'
                    : completionRank <= 5
                      ? `${completionRank}${ordinal(completionRank)} to finish`
                      : `${completionRank}${ordinal(completionRank)} to finish`}
                </p>
              </div>
            ) : (
              <p className="text-slate-400 text-sm mb-4">You've already earned points for this puzzle.</p>
            )}
            <button onClick={resetGame} className="flex items-center gap-2 px-5 py-2.5 bg-primary text-white rounded-xl font-bold mx-auto">
              <span className="material-symbols-outlined" style={{fontSize:'18px'}}>restart_alt</span>
              Play Again
            </button>
          </div>
        )}

        {/* Reset button */}
        {!gameWon && (
          <div className="flex justify-center pb-2">
            <button onClick={resetGame} className="flex items-center gap-2 px-4 py-2 bg-white/10 border border-white/20 text-slate-300 rounded-xl text-sm font-bold">
              <span className="material-symbols-outlined" style={{fontSize:'16px'}}>restart_alt</span>
              Reset Puzzle
            </button>
          </div>
        )}
        </>)}
      </div>
    </div>
  );
}

// ─── Sap Links Puzzle Builder ─────────────────────────────────────────────────
function SapLinksPuzzleBuilder({ onBack, onSubmitPuzzle }) {
  const DEFAULT_NODES = [
    { id: 'n1', word: 'DAISY', given: true,  x: 150, y: 120, firstLetter: '', hint1: '', hint2: '' },
    { id: 'n2', word: 'CHAIN', given: true,  x: 350, y: 120, firstLetter: '', hint1: '', hint2: '' },
    { id: 'n3', word: '',      given: false, x: 550, y: 120, firstLetter: 'L', hint1: '', hint2: '' },
    { id: 'n4', word: '',      given: false, x: 350, y: 280, firstLetter: 'R', hint1: '', hint2: '' },
  ];
  const DEFAULT_EDGES = [
    { id: 'e1', from: 'n1', to: 'n2', phrase: 'daisy chain' },
    { id: 'e2', from: 'n2', to: 'n3', phrase: 'chain link' },
    { id: 'e3', from: 'n2', to: 'n4', phrase: 'chain reaction' },
  ];

  const [nodes, setNodes] = useState(DEFAULT_NODES);
  const [edges, setEdges] = useState(DEFAULT_EDGES);
  const [puzzleInfo, setPuzzleInfo] = useState({ title: 'My Puzzle', difficulty: 'easy', points: 10 });
  const [selectedNode, setSelectedNode] = useState(null);
  const [draggedNode, setDraggedNode] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [showDraftPicker, setShowDraftPicker] = useState(false);
  const [pendingDraft, setPendingDraft] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [flashMsg, setFlashMsg] = useState('');
  const [dragMode, setDragMode] = useState(false);

  useEffect(() => { loadDrafts(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadDrafts = async () => {
    try {
      const d = await storage.get('sap-links-drafts', true);
      if (d) setDrafts(JSON.parse(d.value));
    } catch(e) {}
  };

  const saveDraftsToStorage = async (newDrafts) => {
    await storage.set('sap-links-drafts', JSON.stringify(newDrafts), true);
    setDrafts(newDrafts);
  };

  const flash = (msg) => { setFlashMsg(msg); setTimeout(() => setFlashMsg(''), 3000); };

  const generatePuzzle = () => {
    const solution = {}, hints = {};
    nodes.filter(n => !n.given).forEach(n => {
      solution[n.id] = n.word.toUpperCase() || 'WORD';
      hints[n.id] = [n.hint1 || `${n.word.length || 4} letters`, n.hint2 || 'Another hint'];
    });
    return {
      id: puzzleInfo.title.toLowerCase().replace(/\s+/g, '-'),
      title: puzzleInfo.title,
      difficulty: puzzleInfo.difficulty,
      points: puzzleInfo.points || 10,
      nodes: nodes.map(n => ({
        id: n.id,
        word: n.given ? n.word.toUpperCase() : null,
        given: n.given,
        x: Math.round(n.x), y: Math.round(n.y),
        ...(n.firstLetter && !n.given ? { firstLetter: n.firstLetter } : {}),
      })),
      edges: edges.map(e => ({ from: e.from, to: e.to, phrase: e.phrase })),
      solution, hints,
    };
  };

  const handleSaveDraft = () => {
    const puzzle = generatePuzzle();
    if (drafts.length >= 3) {
      setPendingDraft(puzzle);
      setShowDraftPicker(true);
    } else {
      saveDraftsToStorage([...drafts, { puzzle, savedAt: new Date().toISOString() }]);
      flash('Draft saved!');
    }
  };

  const handleDraftOverwrite = (idx) => {
    const updated = [...drafts];
    updated[idx] = { puzzle: pendingDraft, savedAt: new Date().toISOString() };
    saveDraftsToStorage(updated);
    setShowDraftPicker(false);
    setPendingDraft(null);
    flash('Draft saved!');
  };

  const handleDeleteDraft = (idx) => {
    saveDraftsToStorage(drafts.filter((_, i) => i !== idx));
  };

  const handleLoadDraft = (draft) => {
    const p = draft.puzzle;
    setNodes(p.nodes.map(n => ({ id: n.id, word: n.word || '', given: n.given, x: n.x, y: n.y, firstLetter: n.firstLetter || '', hint1: p.hints?.[n.id]?.[0] || '', hint2: p.hints?.[n.id]?.[1] || '' })));
    setEdges(p.edges.map((e, i) => ({ id: `e${i + 1}`, from: e.from, to: e.to, phrase: e.phrase })));
    setPuzzleInfo({ title: p.title, difficulty: p.difficulty, points: p.points || 10 });
    flash('Draft loaded!');
  };

  const handleSubmitToDaily = async () => {
    const issues = validatePuzzle();
    if (issues.length > 0) { alert('Fix issues before submitting.'); return; }
    await onSubmitPuzzle(generatePuzzle());
    flash('Published to Daily Puzzle!');
  };

  const addNode = () => {
    const id = `n${nodes.length + 1}`;
    setNodes([...nodes, { id, word: '', given: false, x: 200 + Math.random() * 300, y: 100 + Math.random() * 200, firstLetter: '', hint1: '', hint2: '' }]);
  };

  const deleteNode = (id) => {
    setNodes(nodes.filter(n => n.id !== id));
    setEdges(edges.filter(e => e.from !== id && e.to !== id));
    if (selectedNode === id) setSelectedNode(null);
  };

  const updateNode = (id, field, value) => setNodes(nodes.map(n => n.id === id ? { ...n, [field]: value } : n));

  const addEdge = () => {
    const id = `e${edges.length + 1}`;
    setEdges([...edges, { id, from: nodes[0]?.id || '', to: nodes[1]?.id || '', phrase: '' }]);
  };

  const deleteEdge = (id) => setEdges(edges.filter(e => e.id !== id));
  const updateEdge = (id, field, value) => setEdges(edges.map(e => e.id === id ? { ...e, [field]: value } : e));

  const handleSvgMouseMove = (e) => {
    if (!draggedNode) return;
    const rect = e.currentTarget.getBoundingClientRect();
    setNodes(nodes.map(n => n.id === draggedNode ? { ...n, x: e.clientX - rect.left, y: e.clientY - rect.top } : n));
  };

  const handleSvgTouchMove = (e) => {
    if (!draggedNode) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const touch = e.touches[0];
    setNodes(nodes.map(n => n.id === draggedNode ? { ...n, x: touch.clientX - rect.left, y: touch.clientY - rect.top } : n));
  };

  const validatePuzzle = () => {
    const issues = [];
    nodes.forEach(n => {
      if (!n.word && n.given) issues.push(`Node ${n.id}: given nodes must have a word`);
      if (!n.firstLetter && !n.given && !n.word) issues.push(`Node ${n.id}: blank nodes need a first letter`);
    });
    edges.forEach(e => {
      if (!e.phrase) issues.push(`Edge ${e.id}: missing phrase`);
      if (!nodes.find(n => n.id === e.from) || !nodes.find(n => n.id === e.to)) issues.push(`Edge ${e.id}: invalid node refs`);
    });
    return issues;
  };

  const issues = validatePuzzle();
  const inputCls = 'w-full px-2 py-1.5 bg-white/5 border border-primary/20 rounded-lg text-xs text-white placeholder-slate-600 focus:outline-none focus:border-primary';

  return (
    <div className="min-h-full">
      {/* Sub-header */}
      <div className="flex items-center bg-background-dark/80 backdrop-blur-md px-4 py-3 border-b border-primary/10">
        <button onClick={onBack} className="flex size-10 items-center justify-center rounded-full bg-primary/10 text-primary mr-3">
          <span className="material-symbols-outlined">arrow_back</span>
        </button>
        <h2 className="text-white font-bold text-lg flex-1">Puzzle Builder</h2>
        {flashMsg && <span className="text-green-400 text-xs font-bold">{flashMsg}</span>}
      </div>

      {/* Draft overwrite picker */}
      {showDraftPicker && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background-dark/80 backdrop-blur-sm px-6">
          <div className="bg-background-dark border border-primary/20 rounded-2xl p-6 w-full max-w-sm">
            <h3 className="text-white font-bold mb-1">All 3 draft slots are full</h3>
            <p className="text-slate-400 text-sm mb-4">Choose a draft to overwrite:</p>
            <div className="space-y-2 mb-4">
              {drafts.map((d, i) => (
                <button key={i} onClick={() => handleDraftOverwrite(i)}
                  className="w-full flex items-center gap-3 p-3 bg-white/5 border border-primary/10 rounded-xl hover:bg-primary/10 text-left">
                  <span className="material-symbols-outlined text-primary" style={{fontSize:'18px'}}>description</span>
                  <div>
                    <p className="text-white text-sm font-bold">{d.puzzle.title}</p>
                    <p className="text-slate-500 text-xs">{d.puzzle.difficulty} · {new Date(d.savedAt).toLocaleString()}</p>
                  </div>
                </button>
              ))}
            </div>
            <button onClick={() => { setShowDraftPicker(false); setPendingDraft(null); }}
              className="w-full py-2 bg-white/5 border border-white/10 text-slate-400 rounded-xl text-sm">Cancel</button>
          </div>
        </div>
      )}

      <div className="p-4 space-y-4">
        {/* Validation errors */}
        {issues.length > 0 && (
          <div className="bg-red-900/20 border border-red-500/30 rounded-xl p-3">
            <p className="text-red-400 text-xs font-bold mb-1">Issues to fix:</p>
            {issues.map((issue, i) => <p key={i} className="text-red-400/80 text-xs">• {issue}</p>)}
          </div>
        )}

        {/* Puzzle Info */}
        <div className="bg-white/5 border border-primary/10 rounded-xl p-4 space-y-3">
          <h3 className="text-white font-bold text-sm">Puzzle Info</h3>
          <div>
            <label className="block text-xs font-bold text-slate-400 mb-1">Title</label>
            <input type="text" value={puzzleInfo.title}
              onChange={e => setPuzzleInfo({ ...puzzleInfo, title: e.target.value })}
              className={inputCls} placeholder="My Puzzle" />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-400 mb-1">Difficulty</label>
            <select value={puzzleInfo.difficulty}
              onChange={e => setPuzzleInfo({ ...puzzleInfo, difficulty: e.target.value })}
              className={inputCls}>
              <option value="easy">Easy</option>
              <option value="medium">Medium</option>
              <option value="hard">Hard</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-400 mb-1">Max Points (1st place)</label>
            <input type="number" min="1" max="100" value={puzzleInfo.points}
              onChange={e => { const v = parseInt(e.target.value); if (!isNaN(v)) setPuzzleInfo({ ...puzzleInfo, points: Math.max(1, v) }); }}
              className={inputCls} />
            <p className="text-[10px] text-slate-500 mt-1">2nd–5th get 60% · 6th+ get 30%</p>
          </div>
        </div>

        {/* Visual Canvas */}
        <div className="bg-white/5 border border-primary/10 rounded-xl p-3">
          <div className="flex items-center justify-between mb-2 px-1">
            <p className="text-xs font-bold text-slate-400">
              {dragMode ? 'Drag mode — move nodes freely' : 'Scroll to explore · enable drag to reposition'}
            </p>
            <button
              onClick={() => { setDragMode(d => !d); setDraggedNode(null); }}
              className={`flex items-center gap-1 px-3 py-1 rounded-lg text-xs font-bold transition ${
                dragMode
                  ? 'bg-primary text-white shadow-sm shadow-primary/30'
                  : 'bg-white/10 border border-white/20 text-slate-300'
              }`}>
              <span className="material-symbols-outlined" style={{fontSize:'14px'}}>
                {dragMode ? 'pan_tool' : 'open_with'}
              </span>
              {dragMode ? 'Dragging' : 'Move Nodes'}
            </button>
          </div>
          <div className="overflow-x-auto">
            <svg width="700" height="400" className="border border-primary/10 rounded-lg"
              style={{ touchAction: dragMode ? 'none' : 'auto' }}
              onMouseMove={handleSvgMouseMove} onMouseUp={() => setDraggedNode(null)}
              onTouchMove={dragMode ? handleSvgTouchMove : undefined}
              onTouchEnd={dragMode ? () => setDraggedNode(null) : undefined}>
              <defs>
                <pattern id="slb-grid" width="50" height="50" patternUnits="userSpaceOnUse">
                  <path d="M 50 0 L 0 0 0 50" fill="none" stroke="rgba(147,13,242,0.1)" strokeWidth="1" />
                </pattern>
                <marker id="slb-arrow" markerWidth="10" markerHeight="10" refX="9" refY="5" orient="auto">
                  <polygon points="0 0, 10 5, 0 10" fill="#6b7280" />
                </marker>
              </defs>
              <rect width="700" height="400" fill="url(#slb-grid)" />
              {edges.map(edge => {
                const fn = nodes.find(n => n.id === edge.from), tn = nodes.find(n => n.id === edge.to);
                if (!fn || !tn) return null;
                return (
                  <g key={edge.id}>
                    <line x1={fn.x} y1={fn.y} x2={tn.x} y2={tn.y} stroke="#6b7280" strokeWidth="2" markerEnd="url(#slb-arrow)" />
                    <text x={(fn.x + tn.x) / 2} y={(fn.y + tn.y) / 2 - 8} textAnchor="middle" fill="#9ca3af" fontSize="10">
                      {edge.phrase || '???'}
                    </text>
                  </g>
                );
              })}
              {nodes.map(node => (
                <g key={node.id}
                  onMouseDown={e => { if (e.target.tagName === 'circle') setDraggedNode(node.id); }}
                  onTouchStart={dragMode ? (e => { if (e.target.tagName === 'circle') setDraggedNode(node.id); }) : undefined}
                  style={{ cursor: dragMode ? 'move' : 'default' }}>
                  <circle cx={node.x} cy={node.y} r="35"
                    fill={node.given ? '#10b981' : '#60a5fa'}
                    stroke={selectedNode === node.id ? '#f59e0b' : 'transparent'} strokeWidth="3" />
                  <text x={node.x} y={node.y - 10} textAnchor="middle" fill="white" fontSize="10" fontWeight="bold" style={{pointerEvents:'none'}}>{node.id}</text>
                  <text x={node.x} y={node.y + 10} textAnchor="middle" fill="white" fontSize="12" fontWeight="bold" style={{pointerEvents:'none'}}>
                    {node.word || (node.firstLetter ? node.firstLetter + '...' : '?')}
                  </text>
                </g>
              ))}
            </svg>
          </div>
        </div>

        {/* Nodes */}
        <div className="bg-white/5 border border-primary/10 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-white font-bold text-sm">Nodes ({nodes.length})</h3>
            <button onClick={addNode} className="flex items-center gap-1 px-3 py-1.5 bg-primary text-white rounded-xl text-xs font-bold">
              <span className="material-symbols-outlined" style={{fontSize:'14px'}}>add</span>Add
            </button>
          </div>
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {nodes.map(node => (
              <div key={node.id}
                className={`border rounded-xl p-3 cursor-pointer ${selectedNode === node.id ? 'border-primary bg-primary/10' : 'border-primary/10 bg-white/5'}`}
                onClick={() => setSelectedNode(node.id)}>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-xs font-bold text-primary">{node.id}</span>
                  <button onClick={e => { e.stopPropagation(); deleteNode(node.id); }} className="text-slate-500 hover:text-red-400">
                    <span className="material-symbols-outlined" style={{fontSize:'16px'}}>close</span>
                  </button>
                </div>
                <div className="space-y-1.5">
                  <input type="text" placeholder="Word" value={node.word}
                    onChange={e => updateNode(node.id, 'word', e.target.value.toUpperCase())}
                    className={inputCls} />
                  {!node.given && (
                    <>
                      <input type="text" placeholder="First letter hint (e.g. C)" value={node.firstLetter}
                        onChange={e => updateNode(node.id, 'firstLetter', e.target.value.toUpperCase().charAt(0))}
                        maxLength={1} className={inputCls} />
                      <input type="text" placeholder="Hint 1 (e.g. '5 letters')" value={node.hint1}
                        onChange={e => updateNode(node.id, 'hint1', e.target.value)}
                        className={inputCls} />
                      <input type="text" placeholder="Hint 2 (e.g. 'Holds liquid')" value={node.hint2}
                        onChange={e => updateNode(node.id, 'hint2', e.target.value)}
                        className={inputCls} />
                    </>
                  )}
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={node.given}
                      onChange={e => updateNode(node.id, 'given', e.target.checked)} />
                    <span className="text-xs text-slate-400">Given (pre-filled for player)</span>
                  </label>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Edges */}
        <div className="bg-white/5 border border-primary/10 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-white font-bold text-sm">Edges ({edges.length})</h3>
            <button onClick={addEdge} className="flex items-center gap-1 px-3 py-1.5 bg-primary/20 border border-primary/20 text-primary rounded-xl text-xs font-bold">
              <span className="material-symbols-outlined" style={{fontSize:'14px'}}>add</span>Add
            </button>
          </div>
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {edges.map(edge => (
              <div key={edge.id} className="border border-primary/10 bg-white/5 rounded-xl p-3">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-xs font-bold text-slate-400">{edge.id}</span>
                  <button onClick={() => deleteEdge(edge.id)} className="text-slate-500 hover:text-red-400">
                    <span className="material-symbols-outlined" style={{fontSize:'16px'}}>close</span>
                  </button>
                </div>
                <div className="space-y-1.5">
                  <div className="flex gap-2 items-center">
                    <select value={edge.from} onChange={e => updateEdge(edge.id, 'from', e.target.value)} className={`${inputCls} flex-1`}>
                      {nodes.map(n => <option key={n.id} value={n.id}>{n.id} ({n.word || '?'})</option>)}
                    </select>
                    <span className="material-symbols-outlined text-slate-500" style={{fontSize:'14px'}}>arrow_forward</span>
                    <select value={edge.to} onChange={e => updateEdge(edge.id, 'to', e.target.value)} className={`${inputCls} flex-1`}>
                      {nodes.map(n => <option key={n.id} value={n.id}>{n.id} ({n.word || '?'})</option>)}
                    </select>
                  </div>
                  <input type="text" placeholder="Phrase (e.g. 'daisy chain')" value={edge.phrase}
                    onChange={e => updateEdge(edge.id, 'phrase', e.target.value.toLowerCase())}
                    className={inputCls} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Drafts */}
        <div className="bg-white/5 border border-primary/10 rounded-xl p-4">
          <h3 className="text-white font-bold text-sm mb-3">Drafts ({drafts.length}/3)</h3>
          {drafts.length === 0 ? (
            <p className="text-slate-500 text-xs text-center py-3">No saved drafts yet.</p>
          ) : (
            <div className="space-y-2">
              {drafts.map((d, i) => (
                <div key={i} className="flex items-center gap-3 p-3 bg-white/5 border border-primary/10 rounded-xl">
                  <span className="material-symbols-outlined text-primary" style={{fontSize:'18px'}}>description</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-bold truncate">{d.puzzle.title}</p>
                    <p className="text-slate-500 text-xs">{d.puzzle.difficulty} · {new Date(d.savedAt).toLocaleString()}</p>
                  </div>
                  <button onClick={() => handleLoadDraft(d)} className="text-primary text-xs font-bold px-2 py-1 bg-primary/10 rounded-lg hover:bg-primary/20">Load</button>
                  <button onClick={() => handleDeleteDraft(i)} className="text-slate-500 hover:text-red-400">
                    <span className="material-symbols-outlined" style={{fontSize:'16px'}}>close</span>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* JSON Preview */}
        {showPreview && (
          <div className="bg-white/5 border border-primary/10 rounded-xl p-4">
            <pre className="text-xs text-slate-400 overflow-x-auto max-h-56 whitespace-pre-wrap">
              {JSON.stringify(generatePuzzle(), null, 2)}
            </pre>
          </div>
        )}

        {/* Action buttons */}
        <div className="flex flex-col gap-3 pb-6">
          <button onClick={handleSubmitToDaily} disabled={issues.length > 0}
            className={`w-full py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition ${
              issues.length > 0 ? 'bg-white/10 text-slate-600 cursor-not-allowed' : 'bg-primary text-white shadow-lg shadow-primary/30'
            }`}>
            <span className="material-symbols-outlined" style={{fontSize:'18px'}}>publish</span>
            Submit to Daily Puzzle
          </button>
          <div className="flex gap-2">
            <button onClick={handleSaveDraft}
              className="flex-1 py-2.5 bg-white/5 border border-primary/20 text-primary rounded-xl font-bold text-sm flex items-center justify-center gap-1">
              <span className="material-symbols-outlined" style={{fontSize:'16px'}}>save</span>Save Draft
            </button>
            <button onClick={() => setShowPreview(!showPreview)}
              className="flex-1 py-2.5 bg-white/5 border border-white/10 text-slate-300 rounded-xl font-bold text-sm flex items-center justify-center gap-1">
              <span className="material-symbols-outlined" style={{fontSize:'16px'}}>data_object</span>
              {showPreview ? 'Hide JSON' : 'JSON'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Search the Hive ──────────────────────────────────────────────────────────
function HiveGame({ guestName, isAdmin, onBack }) {
  const [challenges, setChallenges] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onSnapshot(doc(db, 'shared', 'hive-challenges'), (snap) => {
      if (snap.exists()) {
        try { setChallenges(JSON.parse(snap.data().value)); } catch (e) { setChallenges([]); }
      } else {
        setChallenges([]);
      }
      setLoading(false);
    }, () => setLoading(false));
    return () => unsubscribe();
  }, []);

  const saveChallenges = async (ch) => {
    try { await storage.set('hive-challenges', JSON.stringify(ch), true); }
    catch (e) { console.error(e); }
  };

  const addChallenge = async (data) => {
    const c = { id: Date.now().toString(), question: data.question, hint: data.hint || '', deadline: data.deadline, revealed: false, correctAnswer: null, submissions: {}, winners: null, createdAt: new Date().toISOString() };
    await saveChallenges([...challenges, c]);
    setShowAddForm(false);
  };

  const submitAnswer = async (challengeId, answer) => {
    const now = new Date().toISOString();
    await saveChallenges(challenges.map(c => c.id === challengeId ? { ...c, submissions: { ...c.submissions, [guestName]: { answer: answer.trim(), submittedAt: now } } } : c));
  };

  const revealAnswer = async (challengeId, correctAnswer) => {
    const ch = challenges.find(c => c.id === challengeId);
    if (!ch) return;
    const deadline = new Date(ch.deadline);
    const correct = Object.entries(ch.submissions)
      .filter(([, sub]) => sub.answer.trim().toLowerCase() === correctAnswer.trim().toLowerCase() && new Date(sub.submittedAt) <= deadline)
      .sort(([, a], [, b]) => new Date(a.submittedAt) - new Date(b.submittedAt))
      .slice(0, 10);
    const winners = [];
    for (let i = 0; i < correct.length; i++) {
      const [name, sub] = correct[i];
      const pts = 10 - i;
      try { await awardPoints(name, pts); } catch (e) {}
      winners.push({ name, rank: i + 1, pts, submittedAt: sub.submittedAt });
    }
    await saveChallenges(challenges.map(c => c.id === challengeId ? { ...c, revealed: true, correctAnswer, winners } : c));
  };

  const deleteChallenge = async (id) => {
    if (window.confirm('Delete this challenge?')) await saveChallenges(challenges.filter(c => c.id !== id));
  };

  const unrevealed = challenges.filter(c => !c.revealed).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  const revealed   = challenges.filter(c =>  c.revealed).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  const active     = unrevealed[0] || null;

  if (loading) return (
    <div className="min-h-full flex items-center justify-center">
      <div className="text-center">
        <span className="material-symbols-outlined text-amber-500/40" style={{fontSize:'48px'}}>emoji_nature</span>
        <p className="text-slate-400 mt-3">Loading the hive...</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-full">
      {/* Header */}
      <div className="flex items-center bg-background-dark/80 backdrop-blur-md px-4 py-3 border-b border-primary/10">
        <button onClick={onBack} className="flex size-10 items-center justify-center rounded-full bg-primary/10 text-primary mr-3">
          <span className="material-symbols-outlined">arrow_back</span>
        </button>
        <div className="flex-1">
          <h2 className="text-white font-bold text-lg leading-tight">Search the Hive</h2>
          <p className="text-slate-500 text-[11px] font-medium uppercase tracking-wider">Daily Challenge</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowInstructions(!showInstructions)} className="flex size-10 items-center justify-center rounded-full bg-primary/10 text-primary">
            <span className="material-symbols-outlined" style={{fontSize:'20px'}}>info</span>
          </button>
          {isAdmin && (
            <button onClick={() => setShowAddForm(!showAddForm)} className="flex size-10 items-center justify-center rounded-full bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400">
              <span className="material-symbols-outlined" style={{fontSize:'20px'}}>add</span>
            </button>
          )}
        </div>
      </div>

      <div className="p-4 space-y-5 pb-8">
        {showInstructions && <HiveInstructions onClose={() => setShowInstructions(false)} />}
        {isAdmin && showAddForm && <HiveChallengeForm onSave={addChallenge} onCancel={() => setShowAddForm(false)} />}

        {active ? (
          <HiveActiveChallenge challenge={active} guestName={guestName} isAdmin={isAdmin} onSubmit={submitAnswer} onReveal={revealAnswer} onDelete={deleteChallenge} />
        ) : (
          <div className="text-center py-16">
            <span className="material-symbols-outlined text-amber-500/20" style={{fontSize:'72px'}}>emoji_nature</span>
            <p className="text-slate-400 mt-3 font-medium">The hive is quiet for now</p>
            <p className="text-slate-500 text-sm mt-1">A new challenge will appear here each day</p>
          </div>
        )}

        {revealed.length > 0 && (
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="h-px flex-1 bg-primary/20" />
              <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Past Challenges</span>
              <div className="h-px flex-1 bg-primary/20" />
            </div>
            <div className="space-y-3">
              {revealed.map(c => <HivePastChallenge key={c.id} challenge={c} guestName={guestName} isAdmin={isAdmin} onDelete={deleteChallenge} />)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function HiveInstructions({ onClose }) {
  return (
    <div className="bg-amber-50 border border-amber-300 dark:bg-amber-950/40 dark:border-amber-500/30 rounded-xl p-5 relative">
      <button onClick={onClose} className="absolute top-3 right-3 text-slate-500">
        <span className="material-symbols-outlined" style={{fontSize:'18px'}}>close</span>
      </button>
      <div className="flex items-center gap-2 mb-3">
        <span className="material-symbols-outlined fill-icon text-amber-600 dark:text-amber-400" style={{fontSize:'22px'}}>emoji_nature</span>
        <h3 className="font-bold text-amber-700 dark:text-amber-300 text-base">How It Works</h3>
      </div>
      <div className="space-y-3 text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
        <p>Each day, a secret question is released to the hive. <span className="text-slate-900 dark:text-white font-medium">Talk to other guests</span> and generate some <span className="text-slate-900 dark:text-white font-medium">buzz</span> to piece together the answer!</p>
        <p>Submit your answer before the deadline. You can resubmit as many times as you like, only your <span className="text-slate-900 dark:text-white font-medium">last answer before the deadline</span> counts.</p>
        <div className="bg-amber-100 dark:bg-black/20 rounded-xl p-4 mt-1">
          <p className="text-xs font-bold uppercase tracking-wider text-amber-700 dark:text-amber-400 mb-2">Points Awarded</p>
          <p className="text-slate-500 dark:text-slate-400 text-xs mb-3">The 10 fastest correct answers win points:</p>
          <div className="grid grid-cols-2 gap-1.5">
            {[{r:'1st',p:10},{r:'2nd',p:9},{r:'3rd',p:8},{r:'4th–10th',p:'7–1'}].map(({r,p}) => (
              <div key={r} className="flex justify-between items-center bg-white/60 dark:bg-white/5 rounded-lg px-3 py-1.5">
                <span className="text-slate-700 dark:text-slate-300 text-xs font-medium">{r}</span>
                <span className="text-amber-700 dark:text-amber-400 text-xs font-black">+{p} pts</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function HiveCountdown({ deadline }) {
  const [timeLeft, setTimeLeft] = useState('');
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    const tick = () => {
      const diff = new Date(deadline) - new Date();
      if (diff <= 0) { setExpired(true); setTimeLeft('Closed'); return; }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setTimeLeft(h > 0 ? `${h}h ${m}m ${s}s` : m > 0 ? `${m}m ${s}s` : `${s}s`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [deadline]);

  return (
    <div className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-bold ${expired ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'}`}>
      <span className="material-symbols-outlined" style={{fontSize:'16px'}}>{expired ? 'lock' : 'timer'}</span>
      <span>{expired ? 'Submissions closed' : `Closes in ${timeLeft}`}</span>
    </div>
  );
}

function HiveActiveChallenge({ challenge, guestName, isAdmin, onSubmit, onReveal, onDelete }) {
  const [answer, setAnswer] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [justSubmitted, setJustSubmitted] = useState(false);
  const [revealInput, setRevealInput] = useState('');
  const [showRevealForm, setShowRevealForm] = useState(false);
  const [showAllSubs, setShowAllSubs] = useState(false);

  const deadline = new Date(challenge.deadline);
  const isPastDeadline = new Date() > deadline;
  const mySubmission = challenge.submissions[guestName];
  const isValidSub = mySubmission && new Date(mySubmission.submittedAt) <= deadline;
  const totalSubs = Object.keys(challenge.submissions).length;

  const formatTime = (iso) => new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  const handleSubmit = async () => {
    if (!answer.trim() || isPastDeadline) return;
    setSubmitting(true);
    await onSubmit(challenge.id, answer.trim());
    setJustSubmitted(true);
    setSubmitting(false);
    setAnswer('');
    setTimeout(() => setJustSubmitted(false), 3000);
  };

  const handleReveal = async () => {
    if (!revealInput.trim()) return;
    await onReveal(challenge.id, revealInput.trim());
  };

  return (
    <div className="space-y-4">
      {/* Challenge card */}
      <div className="rounded-xl overflow-hidden border border-amber-500/20">
        <div className="bg-gradient-to-br from-amber-900/50 via-amber-950/30 to-background-dark px-5 pt-5 pb-4">
          <div className="flex items-center gap-2 mb-3">
            <span className="material-symbols-outlined fill-icon text-amber-300 dark:text-amber-400" style={{fontSize:'16px'}}>emoji_nature</span>
            <span className="text-[10px] font-black uppercase tracking-widest text-amber-300 dark:text-amber-400">Today's Challenge</span>
          </div>
          <p className="text-white text-lg font-bold leading-snug">{challenge.question}</p>
          {challenge.hint && (
            <p className="text-amber-300/50 text-xs mt-2 italic">Hint: {challenge.hint}</p>
          )}
        </div>
        <div className="px-5 pb-5 pt-3 space-y-4 bg-white/2">
          <HiveCountdown deadline={challenge.deadline} />

          {isValidSub && (
            <div className="flex items-start gap-3 bg-primary/10 border border-primary/20 rounded-xl p-3">
              <span className="material-symbols-outlined fill-icon text-primary" style={{fontSize:'18px'}}>check_circle</span>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold text-primary">Answer on record</p>
                <p className="text-sm text-white font-medium">"{mySubmission.answer}"</p>
                <p className="text-[10px] text-slate-500 mt-0.5">Submitted at {formatTime(mySubmission.submittedAt)}</p>
              </div>
            </div>
          )}

          {!isPastDeadline && (
            <div>
              <p className="text-xs text-slate-500 font-medium mb-2">{isValidSub ? 'Change your answer:' : 'Your answer:'}</p>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={answer}
                  onChange={e => setAnswer(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleSubmit(); }}
                  placeholder="Type your answer..."
                  className="flex-1 px-4 py-3 bg-white/5 border border-primary/20 rounded-xl text-white placeholder-slate-600 focus:outline-none focus:border-amber-500/60 text-sm transition-colors"
                />
                <button
                  onClick={handleSubmit}
                  disabled={!answer.trim() || submitting}
                  className="bg-amber-500 text-black font-black px-4 rounded-xl disabled:opacity-40 transition-opacity flex items-center"
                >
                  <span className="material-symbols-outlined fill-icon" style={{fontSize:'20px'}}>{justSubmitted ? 'check_circle' : 'send'}</span>
                </button>
              </div>
              {justSubmitted && <p className="text-xs text-amber-700 dark:text-amber-400 mt-1.5 font-medium">Answer submitted!</p>}
            </div>
          )}

          {isPastDeadline && !isValidSub && (
            <p className="text-sm text-slate-500 text-center py-1">Submissions closed — answer will be revealed soon.</p>
          )}
        </div>
      </div>

      {/* Admin panel */}
      {isAdmin && (
        <div className="bg-primary/5 border border-primary/10 rounded-xl p-4 space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-xs font-bold uppercase tracking-wider text-primary/70">Admin</p>
            <div className="flex items-center gap-3">
              <button onClick={() => setShowAllSubs(!showAllSubs)} className="text-xs text-slate-400 underline">
                {totalSubs} submission{totalSubs !== 1 ? 's' : ''}
              </button>
              <button onClick={() => onDelete(challenge.id)} className="text-slate-600 hover:text-red-400">
                <span className="material-symbols-outlined" style={{fontSize:'16px'}}>delete</span>
              </button>
            </div>
          </div>

          {showAllSubs && totalSubs > 0 && (
            <div className="space-y-1 max-h-48 overflow-y-auto rounded-xl">
              {Object.entries(challenge.submissions)
                .sort(([, a], [, b]) => new Date(a.submittedAt) - new Date(b.submittedAt))
                .map(([name, sub]) => (
                  <div key={name} className="flex items-center gap-2 bg-white/5 rounded-lg px-3 py-2 text-xs">
                    <span className="text-slate-300 font-medium flex-1 truncate">{name}</span>
                    <span className="text-slate-400 truncate max-w-28">"{sub.answer}"</span>
                    <span className="text-slate-600 shrink-0">{formatTime(sub.submittedAt)}</span>
                  </div>
                ))}
            </div>
          )}

          {!showRevealForm ? (
            <button onClick={() => setShowRevealForm(true)} className="w-full bg-amber-100 border border-amber-300 text-amber-700 dark:bg-amber-500/20 dark:border-amber-500/30 dark:text-amber-400 font-bold py-2.5 rounded-xl text-sm">
              Reveal Answer & Award Points
            </button>
          ) : (
            <div className="space-y-2">
              <p className="text-xs text-slate-400">Enter the correct answer:</p>
              <input
                type="text"
                value={revealInput}
                onChange={e => setRevealInput(e.target.value)}
                placeholder="Correct answer..."
                className="w-full px-3 py-2 bg-white/5 border border-amber-500/30 rounded-xl text-sm text-white placeholder-slate-600 focus:outline-none focus:border-amber-500"
              />
              <div className="flex gap-2">
                <button onClick={handleReveal} className="flex-1 bg-amber-500 text-black font-bold py-2 rounded-xl text-sm">Confirm & Award Points</button>
                <button onClick={() => { setShowRevealForm(false); setRevealInput(''); }} className="px-4 bg-white/5 border border-white/10 text-slate-400 py-2 rounded-xl text-sm">Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function HivePastChallenge({ challenge, guestName, isAdmin, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const deadline = new Date(challenge.deadline);
  const mySubmission = challenge.submissions[guestName];
  const isValidSub = mySubmission && new Date(mySubmission.submittedAt) <= deadline;
  const myWin = challenge.winners?.find(w => w.name === guestName);

  return (
    <div className="bg-white/5 border border-primary/10 rounded-xl overflow-hidden">
      <div className="w-full flex items-center gap-3 p-4 text-left cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="flex-1 min-w-0">
          <p className="text-white font-medium text-sm leading-snug truncate">{challenge.question}</p>
          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
            <span className="text-[10px] font-bold text-green-400 bg-green-900/30 px-2 py-0.5 rounded">
              {challenge.correctAnswer}
            </span>
            {myWin && (
              <span className="text-[10px] font-bold text-amber-700 bg-amber-100 dark:text-amber-400 dark:bg-amber-900/30 px-2 py-0.5 rounded">
                #{myWin.rank} place · +{myWin.pts} pts
              </span>
            )}
            {isValidSub && !myWin && (
              <span className="text-[10px] text-slate-500 bg-white/5 px-2 py-0.5 rounded">
                Your answer: {mySubmission.answer}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {isAdmin && (
            <button onClick={e => { e.stopPropagation(); onDelete(challenge.id); }} className="text-slate-600 hover:text-red-400 p-1">
              <span className="material-symbols-outlined" style={{fontSize:'14px'}}>delete</span>
            </button>
          )}
          <span className="material-symbols-outlined text-slate-600" style={{fontSize:'18px'}}>{expanded ? 'expand_less' : 'expand_more'}</span>
        </div>
      </div>

      {expanded && (
        <div className="px-4 pb-4">
          {challenge.winners && challenge.winners.length > 0 ? (
            <div className="space-y-1.5">
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">Top Answers</p>
              {challenge.winners.map(w => (
                <div key={w.name} className={`flex items-center gap-2 rounded-lg px-3 py-2 text-xs ${w.name === guestName ? 'bg-amber-50 border border-amber-200 dark:bg-amber-900/20 dark:border-amber-500/20' : 'bg-black/5 dark:bg-white/5'}`}>
                  <span className={`font-black w-5 shrink-0 ${w.rank === 1 ? 'text-amber-600 dark:text-amber-400' : w.rank <= 3 ? 'text-amber-500 dark:text-amber-300/60' : 'text-slate-500'}`}>#{w.rank}</span>
                  <span className={`flex-1 truncate font-medium ${w.name === guestName ? 'text-amber-700 dark:text-amber-300' : 'text-slate-700 dark:text-slate-300'}`}>{w.name}</span>
                  <span className="text-amber-700 dark:text-amber-400 font-black">+{w.pts}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-500 text-xs text-center py-2">No correct answers this round</p>
          )}
        </div>
      )}
    </div>
  );
}

function HiveChallengeForm({ onSave, onCancel }) {
  const inputCls = "w-full px-3 py-2 bg-white/5 border border-primary/20 rounded-xl text-sm text-white placeholder-slate-600 focus:outline-none focus:border-amber-500/50";
  const defaultDeadline = () => { const d = new Date(); d.setHours(23, 59, 0, 0); return d.toISOString().slice(0, 16); };
  const [form, setForm] = useState({ question: '', hint: '', deadline: defaultDeadline() });

  return (
    <div className="bg-amber-50 border border-amber-200 dark:bg-amber-950/20 dark:border-amber-500/20 rounded-xl p-4 space-y-3">
      <h3 className="font-bold text-amber-700 dark:text-amber-300 text-sm flex items-center gap-2">
        <span className="material-symbols-outlined fill-icon text-amber-600 dark:text-amber-400" style={{fontSize:'18px'}}>emoji_nature</span>
        New Challenge
      </h3>
      <input type="text" placeholder="Question" value={form.question} onChange={e => setForm({...form, question: e.target.value})} className={inputCls} />
      <input type="text" placeholder="Hint (optional)" value={form.hint} onChange={e => setForm({...form, hint: e.target.value})} className={inputCls} />
      <div>
        <p className="text-xs text-slate-500 mb-1 font-medium">Submission deadline</p>
        <input type="datetime-local" value={form.deadline} onChange={e => setForm({...form, deadline: e.target.value})} className={inputCls} />
      </div>
      <div className="flex gap-2">
        <button onClick={() => { if (form.question.trim() && form.deadline) onSave({ ...form, deadline: new Date(form.deadline).toISOString() }); }} className="flex-1 bg-amber-500 text-black font-bold py-2.5 rounded-xl text-sm">Add Challenge</button>
        <button onClick={onCancel} className="px-4 bg-white/5 border border-white/10 text-slate-300 py-2 rounded-xl text-sm">Cancel</button>
      </div>
    </div>
  );
}

// ─── Leaderboard helpers ──────────────────────────────────────────────────────
function LeaderboardRow({ player, rank, guestName, tenant }) {
  const isMe = player.name === guestName;
  const isFirst = rank === 1;
  return (
    <div className={`flex items-center gap-4 p-4 rounded-xl transition ${
      isFirst ? 'bg-primary/20 border border-primary/30' :
      isMe    ? 'bg-white/10 border border-white/20' :
                'bg-white/5 border border-white/5 hover:bg-primary/5'
    }`}>
      <div className="w-8 flex justify-center">
        {isFirst
          ? <span className="material-symbols-outlined text-yellow-400 fill-icon">workspace_premium</span>
          : <span className={`font-bold text-sm ${isMe?'text-primary':'text-slate-500'}`}>{rank}</span>}
      </div>
      <div className="size-10 rounded-full bg-primary/20 border border-primary/30 flex items-center justify-center flex-shrink-0">
        <span className={`font-bold text-sm ${isMe?'text-white':'text-primary'}`}>{getInitials(player.name)}</span>
      </div>
      <div className="flex-1 min-w-0">
        <p className={`font-bold truncate ${isMe?'text-white':'text-slate-100'}`}>{player.name}</p>
        <p className="text-slate-500 text-xs">{getRankTitle(rank, tenant.rankTitles)}</p>
      </div>
      <div className="text-right">
        <p className={`font-bold text-base ${isFirst?'text-primary':isMe?'text-white':'text-slate-300'}`}>{player.points.toLocaleString()}</p>
        <p className="text-[10px] uppercase tracking-wider text-slate-500">pts</p>
      </div>
    </div>
  );
}

function LeaderboardHighlight({ board, guestName, tenant }) {
  const [first, second, third, ...rest] = board;
  const podiumCard = (player, rank) => {
    const isMe = player.name === guestName;
    return (
      <div key={player.name} className={`flex flex-col items-center gap-2 p-4 rounded-xl border ${
        isMe ? 'bg-white/10 border-white/20' : 'bg-white/5 border-white/5'
      }`}>
        <div className="size-10 rounded-full bg-primary/20 border border-primary/30 flex items-center justify-center">
          <span className={`font-bold text-sm ${isMe?'text-white':'text-primary'}`}>{getInitials(player.name)}</span>
        </div>
        <div className="text-center">
          <p className={`font-bold text-sm truncate max-w-[80px] ${isMe?'text-white':'text-slate-100'}`}>{player.name.split(' ')[0]}</p>
          <p className="text-slate-500 text-xs">{getRankTitle(rank, tenant.rankTitles)}</p>
        </div>
        <p className="font-bold text-primary text-sm">{player.points.toLocaleString()} <span className="text-[10px] text-slate-500 font-normal">pts</span></p>
        <span className={`font-bold text-xs ${isMe?'text-primary':'text-slate-500'}`}>#{rank}</span>
      </div>
    );
  };

  return (
    <div className="space-y-3">
      {/* #1 featured wide card */}
      {first && (
        <div className={`flex items-center gap-4 p-5 rounded-xl bg-primary/20 border border-primary/30 ${first.name===guestName?'ring-2 ring-primary/50':''}`}>
          <span className="material-symbols-outlined text-yellow-400 fill-icon text-2xl">workspace_premium</span>
          <div className="size-14 rounded-full bg-primary/30 border-2 border-primary flex items-center justify-center flex-shrink-0">
            <span className="font-black text-lg text-primary">{getInitials(first.name)}</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-bold text-white text-base truncate">{first.name}</p>
            <p className="text-slate-400 text-xs">{getRankTitle(1, tenant.rankTitles)}</p>
          </div>
          <div className="text-right">
            <p className="font-bold text-xl text-primary">{first.points.toLocaleString()}</p>
            <p className="text-[10px] uppercase tracking-wider text-slate-500">pts</p>
          </div>
        </div>
      )}

      {/* #2 and #3 two-column podium */}
      {(second || third) && (
        <div className="grid grid-cols-2 gap-2">
          {second && podiumCard(second, 2)}
          {third  && podiumCard(third,  3)}
        </div>
      )}

      {/* #4+ flat list */}
      {rest.length > 0 && (
        <div className="space-y-2 pt-1">
          {rest.map((player, i) => (
            <LeaderboardRow key={player.name} player={player} rank={i+4} guestName={guestName} tenant={tenant} />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Leaderboard ──────────────────────────────────────────────────────────────
function LeaderboardView({ guestName }) {
  const tenant = useTenant();
  const [board, setBoard] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Layer 4: live onSnapshot subscription — board updates in real-time
    // as points are awarded anywhere in the app. Returns unsubscribe fn.
    const unsub = subscribeLeaderboard((scores) => {
      const sorted = Object.entries(scores)
        .map(([name, points]) => ({ name, points }))
        .sort((a, b) => b.points - a.points);
      setBoard(sorted);
      setLoading(false);
    });
    return unsub;
  }, []);

  const userIdx    = board.findIndex(p => p.name === guestName);
  const userRank   = userIdx >= 0 ? userIdx + 1 : null;
  const userPoints = userIdx >= 0 ? board[userIdx].points : 0;
  const top10Pts   = board.length >= 10 ? board[9].points : 0;
  const gap        = userRank && userRank > 10 ? top10Pts - userPoints : 0;

  return (
    <div className="min-h-full pb-6">
      {/* Header */}
      <div className="flex items-center px-4 pt-5 pb-4 border-b border-primary/10">
        <h2 className="text-white text-xl font-bold flex-1 text-center">Wedding Leaderboard</h2>
        <span className="material-symbols-outlined text-primary">military_tech</span>
      </div>

      {loading ? (
        <div className="text-center py-16 text-slate-400">Loading…</div>
      ) : board.length === 0 ? (
        <div className="text-center py-16">
          <span className="material-symbols-outlined text-primary/30" style={{fontSize:'48px'}}>{tenant.leaderboard?.emptyIcon || 'emoji_events'}</span>
          <p className="text-slate-400 mt-3">{tenant.leaderboard?.emptyTitle || 'No scores yet'}</p>
          <p className="text-primary/50 text-sm mt-1">{tenant.leaderboard?.emptySubtitle || 'Play games to get on the leaderboard'}</p>
        </div>
      ) : (
        <>
          {/* User status card */}
          <div className="p-6 relative overflow-hidden">
            <div className="absolute -top-10 -right-10 size-40 bg-primary/20 rounded-full blur-3xl pointer-events-none" />
            <div className="flex flex-col items-center gap-4 relative z-10">
              <div className="bg-primary/20 p-1 rounded-full">
                <div className="size-24 rounded-full bg-primary/20 border-4 border-primary flex items-center justify-center">
                  <span className="text-primary font-black text-3xl">{getInitials(guestName)}</span>
                </div>
              </div>
              <div className="text-center">
                <p className="text-white text-xl font-bold">
                  {userRank ? `You're in ${userRank}${ordinal(userRank)} Place` : 'Not yet ranked'}
                </p>
                <p className="text-primary/60 text-sm mt-1">
                  {gap > 0 ? `${gap} points to reach Top 10` : userRank ? 'You\'re in the Top 10! 🎉' : 'Play games to earn points!'}
                </p>
              </div>
            </div>
          </div>

          {/* Board list */}
          <div className="px-4">
            <div className="flex items-center gap-2 mb-4">
              <span className="material-symbols-outlined text-primary">{tenant.leaderboard?.boardIcon || 'emoji_events'}</span>
              <h3 className="text-white text-lg font-bold">{tenant.leaderboard?.boardTitle || 'Rankings'}</h3>
            </div>
            {tenant.leaderboard?.layout === 'podium' && board.length > 0 ? (
              <LeaderboardHighlight board={board} guestName={guestName} tenant={tenant} />
            ) : (
              <div className="space-y-2">
                {board.map((player, i) => (
                  <LeaderboardRow key={player.name} player={player} rank={i+1} guestName={guestName} tenant={tenant} />
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function ordinal(n) {
  const s = ['th','st','nd','rd'], v = n%100;
  return (s[(v-20)%10]||s[v]||s[0]);
}

// ─── Settings View ────────────────────────────────────────────────────────────
function SettingsView({ guestName, isAdmin, onNameChange, darkMode, onThemeChange }) {
  const tenant = useTenant();
  const [showPoem, setShowPoem] = useState(false);
  const [newName, setNewName] = useState(guestName);
  const [roomNumber, setRoomNumber] = useState(() => localStorage.getItem('guest-room') || '');
  const [showReset, setShowReset] = useState(false);
  const [showDataReset, setShowDataReset] = useState(false);
  const [dataResetLoading, setDataResetLoading] = useState(false);
  const [notificationsOn, setNotificationsOn] = useState(false);

  // Dark mode toggle is visible only when the tenant hasn't locked colorMode
  const showDarkModeToggle = !tenant.theme.colorMode && tenant.settings?.showDarkModeToggle !== false;

  if (showPoem) return <OurStory onBack={()=>setShowPoem(false)} />;

  const handleNameUpdate = async () => {
    const trimmed = toTitleCase(newName);
    if (!trimmed || trimmed === guestName) return;
    const parts = trimmed.split(' ').filter(Boolean);
    if (parts.length < 2) { alert('Please enter both a first and last name.'); return; }
    try {
      const result = await updateGuestName(guestName, trimmed);
      if (!result.success) {
        alert('This name is already taken by another guest.');
        return;
      }
    } catch(e) {
      console.warn('Could not update registry/leaderboard:', e);
    }
    onNameChange(trimmed, isAdmin);
    alert('Name updated!');
  };

  const handleReset = async () => {
    if (window.confirm('Reset your profile? This won\'t delete shared event or game data.')) {
      try {
        await deregisterGuestName(guestName);
        await storage.delete('guest-profile');
        window.location.reload();
      }
      catch(e) { console.error(e); }
    }
  };

  const handleDataReset = async () => {
    setDataResetLoading(true);
    try {
      // 1. Clear leaderboard
      await storage.set('leaderboard', JSON.stringify({}), true);
      // 2. Clear guest registry
      await storage.set('guest-registry', JSON.stringify([]), true);
      // 3. Delete all sap-links-completions-* docs
      const compKeys = await storage.list('sap-links-completions-', true);
      for (const key of compKeys.keys) {
        await storage.delete(key, true);
      }
      // 4. Reset predictions games — clear all votes and un-reveal questions
      const gamesData = await storage.get('wedding-games', true);
      if (gamesData) {
        const games = JSON.parse(gamesData.value);
        const reset = games.map(g => ({ ...g, predictions: {}, revealed: false, correctAnswer: null }));
        await storage.set('wedding-games', JSON.stringify(reset), true);
      }
      // 5. Reset scavenger challenges — clear submissions and un-reveal
      const challData = await storage.get('scavenger-challenges', true);
      if (challData) {
        const challenges = JSON.parse(challData.value);
        const reset = challenges.map(c => ({ ...c, submissions: {}, revealed: false, correctAnswer: null, winners: [] }));
        await storage.set('scavenger-challenges', JSON.stringify(reset), true);
      }
      setDataResetLoading(false);
      setShowDataReset(false);
      alert('All test data cleared. The app is ready for guests!');
    } catch(e) {
      setDataResetLoading(false);
      alert('Error during reset: ' + e.message);
    }
  };

  return (
    <div className="pb-6">
      {/* Header */}
      <div className="px-6 py-8">
        <h2 className="text-white text-xl font-bold text-center">Settings</h2>
      </div>

      {/* Profile */}
      <div className="px-6 mb-6">
        <div className="flex flex-col items-center gap-4 p-6 rounded-xl bg-primary/10 border border-primary/20">
          <div className="size-24 rounded-full bg-primary/20 border-4 border-primary flex items-center justify-center shadow-lg shadow-primary/20">
            <span className="text-primary font-black text-4xl">{getInitials(guestName)}</span>
          </div>
          <div className="text-center">
            <h3 className="text-xl font-bold text-white">{guestName}</h3>
            <p className="text-primary/60 text-sm font-medium mt-0.5">Wedding Guest · {tenant.appName}</p>
          </div>
          <div className="w-full flex gap-2">
            <input type="text" value={newName} onChange={e=>setNewName(e.target.value)}
              className="flex-1 px-3 py-2 bg-white/5 border border-primary/20 rounded-xl text-white text-sm focus:outline-none focus:border-primary"
              placeholder="Your name"
            />
            <button onClick={handleNameUpdate} className="px-4 bg-primary text-white rounded-xl font-bold text-sm hover:opacity-90 transition">Update</button>
          </div>
          {tenant.settings?.showRoomNumber && (
            <input type="text" value={roomNumber}
              onChange={e=>{ setRoomNumber(e.target.value); localStorage.setItem('guest-room', e.target.value); }}
              className="w-full px-3 py-2 bg-white/5 border border-primary/20 rounded-xl text-white text-sm focus:outline-none focus:border-primary"
              placeholder="Room number (optional)"
            />
          )}
        </div>
      </div>

      {/* Settings groups */}
      <div className="px-6 space-y-6">
        {/* About */}
        {tenant.story?.enabled !== false && (
          <div className="space-y-2">
            <p className="text-xs font-bold uppercase tracking-widest text-primary/40 px-1">About {tenant.appName}</p>
            <button onClick={()=>setShowPoem(true)}
              className="w-full flex items-center justify-between p-4 bg-primary text-white rounded-xl shadow-lg shadow-primary/20">
              <div className="flex items-center gap-3">
                <span className="material-symbols-outlined">auto_stories</span>
                <div className="text-left">
                  <span className="font-bold block">{tenant.story?.label || 'Our Story'}</span>
                  <span className="text-xs text-white/70">The story of {tenant.coupleNames}</span>
                </div>
              </div>
              <span className="material-symbols-outlined">menu_book</span>
            </button>
          </div>
        )}

        {/* Admin */}
        {isAdmin && (
          <div className="space-y-2">
            <p className="text-xs font-bold uppercase tracking-widest text-primary/40 px-1">Admin</p>
            <div className="bg-primary/10 border border-primary/20 rounded-xl p-4 flex items-center gap-3">
              <span className="material-symbols-outlined text-primary">admin_panel_settings</span>
              <div>
                <p className="font-bold text-white text-sm">Admin Access</p>
                <p className="text-primary/60 text-xs">Full management privileges enabled</p>
              </div>
            </div>

            {/* Reset test data */}
            <div className="bg-white/5 border border-amber-500/20 rounded-xl overflow-hidden">
              {!showDataReset ? (
                <button onClick={() => setShowDataReset(true)}
                  className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition">
                  <div className="flex items-center gap-3">
                    <span className="material-symbols-outlined text-amber-400">cleaning_services</span>
                    <div className="text-left">
                      <p className="font-bold text-amber-400 text-sm">Reset Test Data</p>
                      <p className="text-amber-400/60 text-xs">Clear scores, votes & submissions before launch</p>
                    </div>
                  </div>
                  <span className="material-symbols-outlined text-slate-500">chevron_right</span>
                </button>
              ) : (
                <div className="p-4 space-y-3">
                  <div className="flex items-start gap-2">
                    <span className="material-symbols-outlined text-amber-400 mt-0.5" style={{fontSize:'18px'}}>warning</span>
                    <p className="text-sm text-slate-300 leading-relaxed">This will permanently clear:</p>
                  </div>
                  <ul className="text-xs text-slate-400 space-y-1 pl-4 list-disc">
                    <li>All leaderboard scores</li>
                    <li>Guest name registry</li>
                    <li>Sap Links completion records &amp; rankings</li>
                    <li>All prediction votes (questions kept)</li>
                    <li>All scavenger hunt submissions (challenges kept)</li>
                  </ul>
                  <p className="text-xs text-amber-400/70">Events, venues, puzzles, and game questions are preserved.</p>
                  <div className="flex gap-2 pt-1">
                    <button onClick={handleDataReset} disabled={dataResetLoading}
                      className="flex-1 flex items-center justify-center gap-2 bg-amber-600/40 text-amber-300 border border-amber-500/40 py-2 rounded-xl font-bold text-sm disabled:opacity-50">
                      {dataResetLoading
                        ? <><span className="material-symbols-outlined animate-spin" style={{fontSize:'16px'}}>progress_activity</span>Clearing…</>
                        : <><span className="material-symbols-outlined" style={{fontSize:'16px'}}>cleaning_services</span>Confirm Clear</>
                      }
                    </button>
                    <button onClick={() => setShowDataReset(false)} disabled={dataResetLoading}
                      className="px-4 bg-white/5 text-slate-300 py-2 rounded-xl text-sm disabled:opacity-50">
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Preferences — notifications + dark mode (both optional per tenant config) */}
        {(tenant.settings?.showNotificationsToggle || showDarkModeToggle) && (
          <div className="space-y-2">
            <p className="text-xs font-bold uppercase tracking-widest text-primary/40 px-1">Preferences</p>
            {tenant.settings?.showNotificationsToggle && (
              <div className="bg-white/5 border border-primary/10 rounded-xl p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="material-symbols-outlined text-primary">notifications</span>
                  <div>
                    <p className="font-bold text-white text-sm">Push Notifications</p>
                    <p className="text-primary/60 text-xs">Get updates for events &amp; games</p>
                  </div>
                </div>
                <button onClick={()=>setNotificationsOn(n=>!n)} className={`relative w-12 h-6 rounded-full transition-colors duration-300 ${notificationsOn ? 'bg-primary' : 'bg-primary/30'}`}>
                  <span className={`absolute left-0.5 top-0.5 size-5 rounded-full bg-white shadow transition-transform duration-300 ${notificationsOn ? 'translate-x-6' : 'translate-x-0'}`} />
                </button>
              </div>
            )}
            {showDarkModeToggle && (
              <div className="bg-white/5 border border-primary/10 rounded-xl p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="material-symbols-outlined text-primary">{darkMode ? 'dark_mode' : 'light_mode'}</span>
                  <div>
                    <p className="font-bold text-white text-sm">{darkMode ? 'Dark Mode' : 'Light Mode'}</p>
                    <p className="text-primary/60 text-xs">Tap to switch theme</p>
                  </div>
                </div>
                <button onClick={onThemeChange} className={`relative w-12 h-6 rounded-full transition-colors duration-300 ${darkMode ? 'bg-primary' : 'bg-primary/30'}`}>
                  <span className={`absolute left-0.5 top-0.5 size-5 rounded-full bg-white shadow transition-transform duration-300 ${darkMode ? 'translate-x-6' : 'translate-x-0'}`} />
                </button>
              </div>
            )}
          </div>
        )}

        {/* Danger */}
        <div className="space-y-2">
          <p className="text-xs font-bold uppercase tracking-widest text-primary/40 px-1">Account</p>
          <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
            {!showReset ? (
              <button onClick={()=>setShowReset(true)}
                className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition">
                <div className="flex items-center gap-3">
                  <span className="material-symbols-outlined text-red-400">logout</span>
                  <span className="font-medium text-red-400">Reset My Profile</span>
                </div>
                <span className="material-symbols-outlined text-slate-500">chevron_right</span>
              </button>
            ) : (
              <div className="p-4 space-y-3">
                <p className="text-sm text-slate-300">This will clear your name and log you out. Shared event & game data is kept.</p>
                <div className="flex gap-2">
                  <button onClick={handleReset} className="flex-1 bg-red-800/60 text-red-300 py-2 rounded-xl font-bold text-sm">Confirm Reset</button>
                  <button onClick={()=>setShowReset(false)} className="px-4 bg-white/5 text-slate-300 py-2 rounded-xl text-sm">Cancel</button>
                </div>
              </div>
            )}
          </div>
        </div>

        <p className="text-center text-xs text-primary/30 mt-6">
          Version 1.0 · {tenant.appName}<br />Made with love for the Big Day
        </p>
      </div>
    </div>
  );
}

// ─── Our Story ────────────────────────────────────────────────────────────────
// Fetches content from weddings/{weddingId}/story/content and branches on
// format: 'poem' (default, hardcoded fallback), 'text', or 'timeline'.
function OurStory({ onBack }) {
  const tenant = useTenant();
  const [storyDoc, setStoryDoc] = useState(null);
  const [storyLoading, setStoryLoading] = useState(!!tenant.weddingId);

  useEffect(() => {
    if (!tenant.weddingId) return;
    storage.get('story', true)
      .then(result => { if (result?.value) setStoryDoc(JSON.parse(result.value)); })
      .catch(err => console.warn('[OurStory] story fetch failed:', err))
      .finally(() => setStoryLoading(false));
  }, [tenant.weddingId]);

  const format = storyDoc?.format || tenant.story?.format || 'poem';

  const sharedHeader = (
    <div className="sticky top-0 z-10 flex items-center bg-background-dark/80 backdrop-blur-md px-4 py-3 border-b border-primary/10">
      <button onClick={onBack} className="flex size-10 items-center justify-center rounded-full bg-primary/10 text-primary">
        <span className="material-symbols-outlined">arrow_back</span>
      </button>
      <h2 className="text-white font-bold text-lg flex-1 text-center">{tenant.appName}</h2>
      <div className="w-10" />
    </div>
  );

  if (storyLoading) {
    return (
      <div className="min-h-full">
        {sharedHeader}
        <div className="flex items-center justify-center pt-24">
          <span className="material-symbols-outlined text-primary" style={{fontSize:'32px'}}>progress_activity</span>
        </div>
      </div>
    );
  }

  if (format === 'timeline') return <StoryTimeline sharedHeader={sharedHeader} storyDoc={storyDoc} />;
  if (format === 'text')     return <StoryText     sharedHeader={sharedHeader} storyDoc={storyDoc} />;
  return                            <StoryPoem     sharedHeader={sharedHeader} storyDoc={storyDoc} />;
}

// ─── StoryPoem ────────────────────────────────────────────────────────────────
function StoryPoem({ sharedHeader, storyDoc }) {
  const tenant = useTenant();

  const HARDCODED_SECTIONS = [
    {
      section: 'The Seed', icon: 'potted_plant',
      stanzas: [
        ['There once was a young sapling', 'Planted in rich soil, firm in its root.', 'Turned its bright young leaves to the sun', 'It stood small, proud, resolute.'],
        ['But this was no ordinary sapling', "Life decided its journey wouldn't be a breeze \u2014", 'It was going to test the seed, push it to its limit', 'See if it can make it and join the rest of the trees.'],
        ['So just as the small sapling started to grow,', 'The next big step after the initial sprout,', 'Life decided to deal its first test', 'And ripped one of its roots out'],
      ],
      refrain: 'But the sapling decided to still stand.',
    },
    {
      section: 'The Spore', icon: 'coronavirus',
      stanzas: [
        ['It grew shakily, but never wavered', 'Staying true to the roots it still had.', 'It grew into a beautiful, young tree,', 'With its roots thickening ironclad.'],
        ['But then life decided to give its second test,', 'And sent over a deadly, poisonous spore.', 'It sapped the life from the young tree', 'Nearly shriveling it to the floor.'],
      ],
      refrain: 'But the sapling decided to still stand.',
    },
    {
      section: 'The Tree and A Bee', icon: 'park', rightIcon: 'emoji_nature',
      stanzas: [
        ['The sapling found new soil and nutrients', 'Fighting its way from its own doom,', "And despite life's first two tests,", 'It found its way to its very first bloom.'],
        ['The sapling blossomed with beautiful purple flowers,', 'A coveted tree in a land so vast,', 'But the young tree had one more obstacle to face', 'Because life saved its hardest test for last.'],
        ['For the sapling now had to face the challenge of tree-hood', 'And had to learn to grow on its own', 'In an isolated grove by itself,', 'How could the young sapling not feel alone?'],
        ['The sapling faced adversity like never before \u2014', 'Battered by the wind, burned by the sun,', 'Drowned by the rain and drained of its energy,', 'It seemed like life had finally won.'],
        ['Until one day the young sapling was visited by a bee', 'Who landed on its very last flower.', 'It took some pollen and then buzzed away', 'Seemingly robbing the tree of its power.'],
        ['But then the tree stopped to think', 'After everything, how could it still give?', 'And then it dawned on the young sapling \u2014', 'Its strength was found in its will to live.'],
      ],
      refrain: 'So the sapling decided to still stand.',
    },
    {
      section: 'The Forest', icon: 'forest',
      stanzas: [
        ['The young sapling grew into a majestic tree', 'With flowers and fruits bundled aplenty.', 'The strength and power it found at its lowest point', 'Had always been there, hindsight 20/20.'],
        ['For whatever life threw at it, the sapling wouldn\'t give up', 'Shards of glass would crumble to sand', 'Its beauty and strength became tales of legend'],
      ],
      refrain: 'And for the rest of time, the sapling would stand.',
    },
  ];

  const growthStages = [
    { icon: 'potted_plant', size: 30, opacity: 0.35 },
    { icon: 'coronavirus',  size: 46, opacity: 0.55 },
    { icon: 'emoji_nature', size: 66, opacity: 0.75 },
    { icon: 'forest',       size: 86, opacity: 1.00 },
  ];

  // Use sections from doc if available, otherwise fall back to hardcoded
  const sections = (storyDoc?.sections && storyDoc.sections.length > 0)
    ? storyDoc.sections
    : HARDCODED_SECTIONS;

  return (
    <div className="min-h-full">
      {sharedHeader}

      {/* Hero */}
      <div className="relative overflow-hidden bg-primary/10 border-b border-primary/10">
        <div className="absolute inset-0 pointer-events-none"
          style={{background: 'radial-gradient(ellipse at 50% 160%, rgba(var(--color-primary-rgb), 0.22) 0%, transparent 65%)'}} />
        <div className="relative pt-10 pb-6 flex flex-col items-center text-center px-6">
          <span className="bg-primary/20 text-primary px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest mb-4">A Wedding Poem</span>
          <h1 className="text-white text-4xl font-extrabold text-glow leading-tight">{storyDoc?.title || tenant.appName}</h1>
          <p className="mt-2 text-primary/80 font-medium text-sm">The story of {tenant.coupleNames}</p>
        </div>
        <div className="flex items-end justify-around px-6 pb-7 pt-2">
          {growthStages.map((stage, i) => (
            <span key={i} className="material-symbols-outlined text-primary"
              style={{fontSize: `${stage.size}px`, opacity: stage.opacity}}>
              {stage.icon}
            </span>
          ))}
        </div>
      </div>

      {/* Poem sections */}
      <div className="px-6 py-8 space-y-10">
        {sections.map(({ section, icon, rightIcon, stanzas, refrain }) => (
          <div key={section}>
            <div className="flex items-center gap-3 mb-5">
              <div className="h-px flex-1 bg-primary/20" />
              <div className="flex items-center gap-2">
                <span className="material-symbols-outlined text-primary/60" style={{fontSize:'16px'}}>{icon}</span>
                <span className="text-primary text-xs font-bold uppercase tracking-widest">{section}</span>
                {rightIcon && <span className="material-symbols-outlined text-primary/60" style={{fontSize:'16px'}}>{rightIcon}</span>}
              </div>
              <div className="h-px flex-1 bg-primary/20" />
            </div>

            <div className="bg-primary/5 border border-primary/10 rounded-xl overflow-hidden">
              <div className="p-7 space-y-6">
                {stanzas.map((lines, si) => (
                  <p key={si} className="text-base italic leading-relaxed text-slate-200 text-center">
                    {lines.map((line, li) => (
                      <React.Fragment key={li}>
                        {line}{li < lines.length - 1 && <br />}
                      </React.Fragment>
                    ))}
                  </p>
                ))}
              </div>
              {refrain && (
                <div className="mx-6 mb-7 pt-5 border-t border-primary/20 text-center">
                  <p className="text-primary/80 text-sm italic font-semibold tracking-wide">{refrain}</p>
                </div>
              )}
            </div>
          </div>
        ))}

        <div className="flex justify-center pb-6 opacity-40">
          <div className="flex flex-col items-center gap-2">
            <span className="material-symbols-outlined text-primary" style={{fontSize:'36px'}}>forest</span>
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary">{tenant.appName}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── StoryTimeline ────────────────────────────────────────────────────────────
function StoryTimeline({ sharedHeader, storyDoc }) {
  const tenant = useTenant();
  const entries = storyDoc?.entries || [];

  return (
    <div className="min-h-full">
      {sharedHeader}

      {/* Hero */}
      <div className="relative overflow-hidden bg-primary/10 border-b border-primary/10">
        <div className="absolute inset-0 pointer-events-none"
          style={{background: 'radial-gradient(ellipse at 50% 160%, rgba(var(--color-primary-rgb), 0.20) 0%, transparent 65%)'}} />
        <div className="relative pt-10 pb-10 flex flex-col items-center text-center px-6">
          <span className="bg-primary/20 text-primary px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest mb-4">
            {tenant.story?.label || 'Our Story'}
          </span>
          <h1 className="text-white text-4xl font-extrabold text-glow leading-tight mb-2">
            {storyDoc?.title || tenant.coupleNames}
          </h1>
          <p className="text-primary/70 text-sm font-medium">{tenant.coupleNames}</p>
        </div>
      </div>

      {/* Timeline entries */}
      <div className="px-5 py-8">
        <div className="relative">
          {/* Vertical connecting line */}
          <div className="absolute left-[18px] top-3 bottom-3 w-0.5 bg-primary/25 rounded-full" />

          <div className="space-y-7">
            {entries.map((entry, i) => (
              <div key={i} className="relative flex gap-5">
                {/* Dot with ring */}
                <div className="relative z-10 flex-shrink-0 mt-1.5">
                  <div className="w-[22px] h-[22px] rounded-full bg-primary flex items-center justify-center"
                    style={{boxShadow: '0 0 0 4px var(--color-dark-bg)'}}>
                    <div className="w-2 h-2 rounded-full bg-white/80" />
                  </div>
                </div>

                {/* Content card */}
                <div className="flex-1 bg-primary/5 border border-primary/10 rounded-xl overflow-hidden mb-1">
                  {entry.imageUrl && (
                    <img src={entry.imageUrl} alt={entry.title}
                      className="w-full object-cover"
                      style={{maxHeight: '180px'}} />
                  )}
                  <div className="p-5">
                    <span className="inline-block bg-primary/20 text-primary text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-widest mb-3">
                      {entry.year || entry.date}
                    </span>
                    <h3 className="text-white text-lg font-bold leading-snug mb-2">{entry.title}</h3>
                    <p className="text-slate-300 text-sm leading-relaxed">{entry.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-center pt-8 pb-6 opacity-40">
          <div className="flex flex-col items-center gap-2">
            <span className="material-symbols-outlined text-primary" style={{fontSize:'36px'}}>
              {tenant.theme?.motifIcon || 'favorite'}
            </span>
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary">{tenant.appName}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── StoryText ────────────────────────────────────────────────────────────────
function StoryText({ sharedHeader, storyDoc }) {
  const tenant = useTenant();
  const entries = storyDoc?.entries || [];

  return (
    <div className="min-h-full">
      {sharedHeader}

      <div className="relative overflow-hidden bg-primary/10 border-b border-primary/10">
        <div className="relative pt-10 pb-8 flex flex-col items-center text-center px-6">
          <span className="bg-primary/20 text-primary px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest mb-4">
            {tenant.story?.label || 'Our Story'}
          </span>
          <h1 className="text-white text-4xl font-extrabold text-glow leading-tight">
            {storyDoc?.title || tenant.coupleNames}
          </h1>
        </div>
      </div>

      <div className="px-6 py-8 space-y-6">
        {entries.map((entry, i) => (
          <div key={i} className="bg-primary/5 border border-primary/10 rounded-xl overflow-hidden">
            {entry.imageUrl && (
              <img src={entry.imageUrl} alt={entry.heading || ''}
                className="w-full object-cover"
                style={{maxHeight: '200px'}} />
            )}
            <div className="p-6">
              {entry.heading && (
                <h3 className="text-white text-lg font-bold mb-3">{entry.heading}</h3>
              )}
              <p className="text-slate-200 text-sm leading-relaxed">{entry.body}</p>
            </div>
          </div>
        ))}

        <div className="flex justify-center pb-6 opacity-40">
          <span className="material-symbols-outlined text-primary" style={{fontSize:'36px'}}>favorite</span>
        </div>
      </div>
    </div>
  );
}

// ─── Bottom Navigation ────────────────────────────────────────────────────────
function BottomNav({ currentView, setCurrentView }) {
  const items = [
    { id:'home',        icon:'home',          label:'Home'     },
    { id:'events',      icon:'calendar_month', label:'Events'   },
    { id:'games',       icon:'sports_esports', label:'Games'    },
    { id:'leaderboard', icon:'leaderboard',    label:'Ranks'    },
    { id:'settings',    icon:'settings',       label:'Settings' },
  ];

  return (
    <nav className="fixed bottom-0 left-0 right-0 max-w-md mx-auto bg-background-dark/90 backdrop-blur-xl border-t border-primary/10 px-2 pb-6 pt-3 flex justify-around items-center z-50">
      {items.map(item => {
        const active = currentView === item.id;
        return (
          <button key={item.id} onClick={()=>setCurrentView(item.id)}
            className={`flex flex-col items-center gap-1 transition ${active?'text-primary':'text-slate-500 hover:text-slate-300'}`}>
            <span className={`material-symbols-outlined ${active?'fill-icon':''}`} style={{fontSize:'26px'}}>{item.icon}</span>
            <span className={`text-[10px] font-bold uppercase tracking-wide ${active?'text-primary':'text-slate-500'}`}>{item.label}</span>
          </button>
        );
      })}
    </nav>
  );
}
